import React, { createContext, useContext, useState, Fragment, useMemo, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';

var AreaShapeType;

(function (AreaShapeType) {
  AreaShapeType["RECT"] = "RECT";
  AreaShapeType["CIRCLE"] = "CIRCLE";
  AreaShapeType["POLYGON"] = "POLYGON";
})(AreaShapeType || (AreaShapeType = {}));

var ActionType;

(function (ActionType) {
  ActionType["MANUAL_DEEP_LINK"] = "MANUAL_DEEP_LINK";
  ActionType["PDP_DEEP_LINK"] = "PDP_DEEP_LINK";
  ActionType["STORE_HOME_DEEP_LINK"] = "STORE_HOME_DEEP_LINK";
  ActionType["EVENT_METADATA"] = "EVENT_METADATA";
  ActionType["MARKETING_CONSENT"] = "MARKETING_CONSENT";
  ActionType["KAKAO_SHARE"] = "KAKAO_SHARE";
  ActionType["FACEBOOK_SHARE"] = "FACEBOOK_SHARE";
  ActionType["URL_SHARE"] = "URL_SHARE";
  ActionType["ADD_FAVORITE_STORE"] = "ADD_FAVORITE_STORE";
})(ActionType || (ActionType = {}));

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

var SiteId;

(function (SiteId) {
  SiteId[SiteId["ZIGZAG"] = 1] = "ZIGZAG";
  SiteId[SiteId["POSTY"] = 2] = "POSTY";
  SiteId[SiteId["FASHION_BY_KAKAO"] = 3] = "FASHION_BY_KAKAO";
})(SiteId || (SiteId = {}));

var ContentLanguage;

(function (ContentLanguage) {
  ContentLanguage["en"] = "en";
  ContentLanguage["ja"] = "ja";
  ContentLanguage["ko"] = "ko";
})(ContentLanguage || (ContentLanguage = {}));

var ContentCurrency;

(function (ContentCurrency) {
  ContentCurrency["CAD"] = "CAD";
  ContentCurrency["JPY"] = "JPY";
  ContentCurrency["KRW"] = "KRW";
  ContentCurrency["USD"] = "USD";
})(ContentCurrency || (ContentCurrency = {}));

var ContentCountry;

(function (ContentCountry) {
  ContentCountry["CA"] = "CA";
  ContentCountry["JP"] = "JP";
  ContentCountry["KR"] = "KR";
  ContentCountry["US"] = "US";
})(ContentCountry || (ContentCountry = {}));

var ContentEditorAgent;

(function (ContentEditorAgent) {
  ContentEditorAgent["APP"] = "APP";
  ContentEditorAgent["WEB"] = "WEB";
})(ContentEditorAgent || (ContentEditorAgent = {}));

var ContentEditorType;

(function (ContentEditorType) {
  ContentEditorType["DEFAULT"] = "DEFAULT";
  ContentEditorType["MAGAZINE"] = "MAGAZINE";
})(ContentEditorType || (ContentEditorType = {}));

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var UserInputContext = /*#__PURE__*/createContext({
  event_metadata: ['', function () {
    return undefined;
  }]
});
UserInputContext.displayName = 'UserInputContext';
function useUserInput(namespace) {
  var context = useContext(UserInputContext);
  return context[namespace];
}
function useUserInputState() {
  return {
    event_metadata: useState(undefined)
  };
}

var _excluded$4 = ["custom_data"];
var ContentMetadataContext = /*#__PURE__*/createContext({
  current_time: 0,
  date_started: 0,
  date_ended: 0,
  uuid: '',
  page_title: '',
  page_url: '',
  site_id: SiteId.ZIGZAG,
  language: ContentLanguage.ko,
  country: ContentCountry.KR,
  currency: ContentCurrency.KRW,
  support_agent_list: [ContentEditorAgent.APP],
  type: ContentEditorType.DEFAULT,
  title: '',
  description: '',
  button_title: '',
  image_src: '',
  custom_data: {}
});
ContentMetadataContext.displayName = 'ContentMetadataContext';
function useContentMetadata() {
  var _useContext = useContext(ContentMetadataContext),
      custom_data = _useContext.custom_data,
      values = _objectWithoutPropertiesLoose(_useContext, _excluded$4);

  var is_before = values.current_time < values.date_started;
  var is_after = values.current_time > values.date_ended;
  var event_metadata_user_input = useUserInput('event_metadata');
  return _extends({
    is_before: is_before,
    is_after: is_after,
    event_metadata_user_input: event_metadata_user_input,
    custom_data: custom_data
  }, values);
}

var ModuleTree = function ModuleTree(_ref) {
  var component_map = _ref.component_map,
      component_relations = _ref.component_relations;
  return /*#__PURE__*/React.createElement(Fragment, null, component_relations.map(function (_ref2) {
    var uuid = _ref2.uuid,
        children = _ref2.children;
    return /*#__PURE__*/React.createElement(ModuleMapper, _extends({
      key: uuid,
      child_modules: children
    }, component_map[uuid]));
  }));
};

function isImageCarouselModule(currentModule) {
  return currentModule.__type === 'ImageCarousel';
}
function isImageMapModule(currentModule) {
  return currentModule.__type === 'ImageMap';
}
function isImageSlideModule(currentModule) {
  return currentModule.__type === 'ImageSlide';
}
function isImageRailModule(currentModule) {
  return currentModule.__type === 'ImageRail';
}
function isImageModule(currentModule) {
  return isImageCarouselModule(currentModule) || isImageMapModule(currentModule) || isImageSlideModule(currentModule) || isImageRailModule(currentModule);
}
function isMaxImageAspectRatioModule(currentModule) {
  return isImageCarouselModule(currentModule) || isImageSlideModule(currentModule) || isImageRailModule(currentModule);
}
function isSumImageAspectRatioModule(currentModule) {
  return isImageMapModule(currentModule);
}
function isSlotModule(currentModule) {
  return currentModule.__type === 'Slot';
}
function isFloatingButtonModule(currentModule) {
  return currentModule.__type === 'FloatingButton';
}
function isProductTabModule(currentModule) {
  return currentModule.__type === 'ProductTab';
}
function isProductListModule(currentModule) {
  return currentModule.__type === 'ProductList';
}
function isProductFilterModule(currentModule) {
  return currentModule.__type === 'ProductFilter';
}
function isProductCarouselModule(currentModule) {
  return currentModule.__type === 'ProductCarousel';
}
function isProductModule(currentModule) {
  return isProductTabModule(currentModule) || isProductListModule(currentModule) || isProductFilterModule(currentModule) || isProductCarouselModule(currentModule);
}
function isContentReplyModule(currentModule) {
  return currentModule.__type === 'ContentReply';
}
function isStickerGroupModule(currentModule) {
  return currentModule.__type === 'StickerGroup';
}
function isStoreGridModule(currentModule) {
  return currentModule.__type === 'StoreGrid';
}
function isTabAreaModule(currentModule) {
  return currentModule.__type === 'TabArea';
}
function isScrollableTabAreaModule(currentModule) {
  return currentModule.__type === 'ScrollableTabArea';
}
function isTabModule(currentModule) {
  return isTabAreaModule(currentModule) || isScrollableTabAreaModule(currentModule);
}
function isTabContentModule(currentModule) {
  return currentModule.__type === 'TabContent';
}
function isVideoModule(currentModule) {
  return currentModule.__type === 'Video';
}
function isYouTubeModule(currentModule) {
  return currentModule.__type === 'YouTube';
}
function isVideoLikeModule(currentModule) {
  return isVideoModule(currentModule) || isYouTubeModule(currentModule);
}
function isAtfCandidateModule(currentModule) {
  return isImageModule(currentModule) || isVideoLikeModule(currentModule) || isProductModule(currentModule);
}

function touchPayloadWithIndexedComponents(_ref) {
  var payload = _ref.payload;

  try {
    var incrementalModuleIndexAtDocumentRef = {
      current: 0
    };
    var atfModuleFoundRef = {
      current: false
    };
    var touchComponentWithIndexData = getComponentWithIndexDataToucher({
      payload: payload,
      incrementalModuleIndexAtDocumentRef: incrementalModuleIndexAtDocumentRef,
      atfModuleFoundRef: atfModuleFoundRef
    });
    payload.component_relations.forEach(touchComponentWithIndexData);
  } catch (error) {
    console.error(error);
  }
}

function getComponentWithIndexDataToucher(_ref2) {
  var payload = _ref2.payload,
      incrementalModuleIndexAtDocumentRef = _ref2.incrementalModuleIndexAtDocumentRef,
      atfModuleFoundRef = _ref2.atfModuleFoundRef;
  return function touchComponentWithIndexData(component_relation, __index_at_parent) {
    try {
      var _component_relation$c;

      var __index_at_document = incrementalModuleIndexAtDocumentRef.current++;

      var __is_atf_module = atfModuleFoundRef.current === false && isAtfCandidateModule(payload.component_map[component_relation.uuid]);

      if (__is_atf_module) {
        atfModuleFoundRef.current = true;
      }

      if (component_relation.uuid in payload.component_map) {
        payload.component_map[component_relation.uuid].__index_at_parent = __index_at_parent;
        payload.component_map[component_relation.uuid].__index_at_document = __index_at_document;
        payload.component_map[component_relation.uuid].__is_atf_module = __is_atf_module;
      }

      component_relation.__index_at_parent = __index_at_parent;
      component_relation.__index_at_document = __index_at_document;
      component_relation.__is_atf_module = __is_atf_module;
      (_component_relation$c = component_relation.children) == null ? void 0 : _component_relation$c.forEach(touchComponentWithIndexData);
    } catch (error) {
      console.error(error);
    }
  };
}

var getVisibleBetweenFilter = function getVisibleBetweenFilter(_ref) {
  var current_time = _ref.current_time,
      component_map = _ref.component_map;
  return function (_ref2) {
    var uuid = _ref2.uuid;

    try {
      var visible_between = component_map[uuid].visible_between;

      if (!visible_between) {
        return true;
      }

      var start_time = visible_between[0],
          end_time = visible_between[1];
      return current_time >= start_time && current_time < end_time;
    } catch (error) {
      console.error(error);
      return true;
    }
  };
};
function touchPayloadWithVisibleBetweenFilter(_ref3) {
  var payload = _ref3.payload;

  try {
    var component_map = payload.component_map,
        component_relations = payload.component_relations;
    var current_time = Date.now();
    var visibleBetweenFilter = getVisibleBetweenFilter({
      current_time: current_time,
      component_map: component_map
    });
    payload.component_relations = getFilteredComponentRelations({
      filter: visibleBetweenFilter,
      component_relations: component_relations
    });
    Object.entries(payload.component_map).forEach(function (_ref4) {
      var uuid = _ref4[0],
          __type = _ref4[1].__type;
      var isVisible = visibleBetweenFilter({
        uuid: uuid,
        __type: __type
      });

      if (!isVisible) {
        delete payload.component_map[uuid];
      }
    });
  } catch (error) {
    console.error(error);
  }
}

function getFilteredComponentRelations(_ref5) {
  var filter = _ref5.filter,
      component_relations = _ref5.component_relations;
  return component_relations.filter(filter).map(function (component_relation) {
    try {
      if (component_relation.children) {
        component_relation.children = getFilteredComponentRelations({
          filter: filter,
          component_relations: component_relation.children
        });
      }

      return component_relation;
    } catch (error) {
      console.error(error);
      return component_relation;
    }
  });
}

function parseContentEditorItemPayloadWithOptions(_ref) {
  var content_editor_json = _ref.content_editor_json,
      touchPayloadForServiceOptimization = _ref.touchPayloadForServiceOptimization;

  try {
    var result = JSON.parse(content_editor_json);

    if (!result.component_map || !result.component_relations) {
      throw new Error('invalid format');
    }

    var assertResult = result;

    if (touchPayloadForServiceOptimization) {
      touchPayloadWithVisibleBetweenFilter({
        payload: assertResult
      });
      touchPayloadWithIndexedComponents({
        payload: assertResult
      });
    }

    return assertResult;
  } catch (err) {
    return {
      component_map: {},
      component_relations: []
    };
  }
}
/** 최적화를 위해 페이로드를 그대로 사용하지 않고 정제하여 사용, 서비스 전용 */


function parseContentEditorItemPayload(content_editor_json) {
  return parseContentEditorItemPayloadWithOptions({
    content_editor_json: content_editor_json,
    touchPayloadForServiceOptimization: true
  });
}
/** 정제하지 않은 페이로드를 그대로 사용하기 때문에 백오피스에서 사용하기 적합 */

function parseContentEditorItemPayloadForBackoffice(content_editor_json) {
  return parseContentEditorItemPayloadWithOptions({
    content_editor_json: content_editor_json,
    touchPayloadForServiceOptimization: false
  });
}
function getContentEditorItemCustomLink(payload) {
  var _payload$custom_link, _payload$share_info;

  return (_payload$custom_link = payload.custom_link) != null ? _payload$custom_link : (_payload$share_info = payload.share_info) == null ? void 0 : _payload$share_info.__custom_link;
}
var ComponentMapContext = /*#__PURE__*/createContext({});
ComponentMapContext.displayName = 'ComponentMapContext';
function useComponentMap(_temp) {
  var _ref2 = _temp === void 0 ? {} : _temp,
      _ref2$use_visible_bet = _ref2.use_visible_between_option,
      use_visible_between_option = _ref2$use_visible_bet === void 0 ? false : _ref2$use_visible_bet,
      component_map_props = _ref2.component_map;

  var _useContentMetadata = useContentMetadata(),
      current_time = _useContentMetadata.current_time;

  var component_map_context = useContext(ComponentMapContext);
  var component_map = component_map_props || component_map_context;

  if (use_visible_between_option) {
    return Object.keys(component_map).filter(function (uuid) {
      var visible_between = component_map[uuid].visible_between;

      if (!visible_between) {
        return true;
      }

      var start_time = visible_between[0],
          end_time = visible_between[1];
      return current_time >= start_time && current_time < end_time;
    }).reduce(function (acc, uuid) {
      acc[uuid] = component_map[uuid];
      return acc;
    }, {});
  }

  return component_map;
}

var ModuleHierarchyContext = /*#__PURE__*/createContext({
  modules: []
});
ModuleHierarchyContext.displayName = 'ModuleHierarchyContext';
function useModuleHierarchy() {
  return useContext(ModuleHierarchyContext);
}

var styles$m = {
  "wrapper": "Skeleton-module__wrapper___25_9k"
};
var Skeleton = function Skeleton(props) {
  return /*#__PURE__*/React.createElement("div", {
    className: styles$m.wrapper
  }, props.name);
};

var AnimationModule = function AnimationModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "Animation"
  });
};
var ClipType;

(function (ClipType) {
  ClipType["MEDIA"] = "MEDIA";
  ClipType["IMAGE_CAROUSEL"] = "IMAGE_CAROUSEL";
  ClipType["IMAGE_SLIDE"] = "IMAGE_SLIDE";
})(ClipType || (ClipType = {}));

var EffectType;

(function (EffectType) {
  EffectType["FadeIn"] = "FadeIn";
  EffectType["FadeOut"] = "FadeOut";
  EffectType["MoveIn"] = "MoveIn";
  EffectType["MoveOut"] = "MoveOut";
  EffectType["Floating"] = "Floating";
  EffectType["SwipeIn"] = "SwipeIn";
  EffectType["SwipeOut"] = "SwipeOut";
  EffectType["ScaleIn"] = "ScaleIn";
  EffectType["ScaleOut"] = "ScaleOut";
})(EffectType || (EffectType = {}));

var EFFECT_NAME = {
  FadeIn: 'Fade In',
  FadeOut: 'Fade Out',
  MoveIn: 'Move In',
  MoveOut: 'Move Out',
  Floating: 'Floating',
  SwipeIn: 'Swipe In',
  SwipeOut: 'Swipe Out',
  ScaleIn: 'Scale In',
  ScaleOut: 'Scale Out'
};
var EFFECT_ORDER = {
  FadeIn: 100,
  FadeOut: 200,
  MoveIn: 300,
  MoveOut: 400,
  Floating: 500,
  SwipeIn: 600,
  SwipeOut: 700,
  ScaleIn: 800,
  ScaleOut: 900
};
var MoveDirection;

(function (MoveDirection) {
  MoveDirection["UP"] = "UP";
  MoveDirection["DOWN"] = "DOWN";
  MoveDirection["LEFT"] = "LEFT";
  MoveDirection["RIGHT"] = "RIGHT";
})(MoveDirection || (MoveDirection = {}));

var FloatingDirection;

(function (FloatingDirection) {
  FloatingDirection["HORIZONTAL"] = "HORIZONTAL";
  FloatingDirection["VERTICAL"] = "VERTICAL";
})(FloatingDirection || (FloatingDirection = {}));

var SwipeDirection;

(function (SwipeDirection) {
  SwipeDirection["UP"] = "UP";
  SwipeDirection["DOWN"] = "DOWN";
  SwipeDirection["LEFT"] = "LEFT";
  SwipeDirection["RIGHT"] = "RIGHT";
  SwipeDirection["LEFT_AND_RIGHT"] = "LEFT_AND_RIGHT";
  SwipeDirection["TOP_AND_BOTTOM"] = "TOP_AND_BOTTOM";
  SwipeDirection["CENTER"] = "CENTER";
  SwipeDirection["OUTER"] = "OUTER";
})(SwipeDirection || (SwipeDirection = {}));

var ScaleDirection;

(function (ScaleDirection) {
  ScaleDirection["CENTER"] = "CENTER";
})(ScaleDirection || (ScaleDirection = {}));

var VariantKey;

(function (VariantKey) {
  VariantKey["before"] = "before";
  VariantKey["in"] = "in";
  VariantKey["after"] = "after";
})(VariantKey || (VariantKey = {}));

var AnimationSceneModule = function AnimationSceneModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "AnimationScene"
  });
};

var DAY_IN_MILLIS = 86400000;
var HOUR_IN_MILLIS = 3600000;
var MINUTE_IN_MILLIS = 60000;
var SECOND_IN_MILLIS = 1000;
function useTimer(options) {
  var _useState = useState(0),
      force_update_token = _useState[0],
      forceUpdate = _useState[1];

  var timer = useMemo(function () {
    var now = Date.now();
    var diff = options.end_time - now;
    var in_progress = diff > 0;

    if (!in_progress) {
      return {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
        millis: 0,
        in_progress: in_progress
      };
    }

    var days = Math.floor(diff / DAY_IN_MILLIS);
    diff = diff % DAY_IN_MILLIS;
    var hours = Math.floor(diff / HOUR_IN_MILLIS);
    diff = diff % HOUR_IN_MILLIS;
    var minutes = Math.floor(diff / MINUTE_IN_MILLIS);
    diff = diff % MINUTE_IN_MILLIS;
    var seconds = Math.floor(diff / SECOND_IN_MILLIS);
    diff = diff % SECOND_IN_MILLIS;
    return {
      days: days,
      hours: hours,
      minutes: minutes,
      seconds: seconds,
      millis: diff,
      in_progress: in_progress
    };
  }, [force_update_token, options.end_time]);
  useEffect(function () {
    if (timer.in_progress) {
      var timeout = setTimeout(function () {
        return forceUpdate(function (v) {
          return v + 1;
        });
      }, timer.millis);
      return function () {
        return clearTimeout(timeout);
      };
    }
  }, [timer]);
  return timer;
}

var styles$l = {
  "wrapper": "BasicTimer-module__wrapper___2m5II",
  "timer": "BasicTimer-module__timer___3ucBr",
  "timer-item": "BasicTimer-module__timer-item___2Y1-u"
};
var BasicTimerModule = function BasicTimerModule(props) {
  var _useTimer = useTimer({
    end_time: props.end_time
  }),
      days = _useTimer.days,
      hours = _useTimer.hours,
      minutes = _useTimer.minutes,
      seconds = _useTimer.seconds,
      in_progress = _useTimer.in_progress;

  var text_style = {
    textAlign: 'center',
    color: props.text_color
  };
  return /*#__PURE__*/React.createElement("div", {
    className: styles$l.wrapper,
    style: {
      backgroundColor: props.background_color
    }
  }, in_progress && props.progress_text && /*#__PURE__*/React.createElement("div", {
    style: text_style
  }, props.progress_text), !in_progress && /*#__PURE__*/React.createElement("div", {
    style: text_style
  }, props.end_time), /*#__PURE__*/React.createElement("div", {
    className: styles$l.timer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$l['timer-item']
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      color: props.timer_color,
      fontWeight: props.timer_weight === 'bold' ? 500 : 400
    }
  }, days), /*#__PURE__*/React.createElement("span", {
    style: {
      color: props.unit_color
    }
  }, "DAYS")), /*#__PURE__*/React.createElement("div", {
    className: styles$l['timer-item']
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      color: props.timer_color,
      fontWeight: props.timer_weight === 'bold' ? 500 : 400
    }
  }, hours), /*#__PURE__*/React.createElement("span", {
    style: {
      color: props.unit_color
    }
  }, "HRS")), /*#__PURE__*/React.createElement("div", {
    className: styles$l['timer-item']
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      color: props.timer_color,
      fontWeight: props.timer_weight === 'bold' ? 500 : 400
    }
  }, minutes), /*#__PURE__*/React.createElement("span", {
    style: {
      color: props.unit_color
    }
  }, "MINS")), /*#__PURE__*/React.createElement("div", {
    className: styles$l['timer-item']
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      color: props.timer_color,
      fontWeight: props.timer_weight === 'bold' ? 500 : 400
    }
  }, seconds), /*#__PURE__*/React.createElement("span", {
    style: {
      color: props.unit_color
    }
  }, "SECS"))));
};

var BlackWeekBestModule = function BlackWeekBestModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "BlackWeekBest"
  });
};

var BlackWeekSpecialPriceModule = function BlackWeekSpecialPriceModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "BlackWeekSpecialPrice"
  });
};

var BlackWeekSpecialPriceProductListModule = function BlackWeekSpecialPriceProductListModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "BlackWeekSpecialPriceProductList"
  });
};

var BlackWeekSpecialPriceSubscribeButtonModule = function BlackWeekSpecialPriceSubscribeButtonModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "BlackWeekSpecialPriceSubscribeButton"
  });
};

var BoxItemModule = function BoxItemModule(_ref) {
  var uuid = _ref.uuid,
      children = _ref.children,
      module_background_color = _ref.module_background_color;
  return /*#__PURE__*/React.createElement("div", {
    id: uuid,
    style: {
      backgroundColor: module_background_color
    }
  }, children);
};

var ContentReplyModule = function ContentReplyModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "ContentReply"
  });
};

var styles$k = {
  "wrapper": "DescriptiveTimer-module__wrapper___bc0g2",
  "frame": "DescriptiveTimer-module__frame___2EXzR",
  "title": "DescriptiveTimer-module__title___PaF4A",
  "timer": "DescriptiveTimer-module__timer___fIMJ7"
};

var initialGetDescriptiveTimerText = function initialGetDescriptiveTimerText(_ref) {
  var days = _ref.days,
      hours = _ref.hours,
      minutes = _ref.minutes,
      seconds = _ref.seconds;

  if (days > 0) {
    return days + "\uC77C " + hours + "\uC2DC\uAC04 " + minutes + "\uBD84 " + seconds + "\uCD08";
  }

  if (hours > 0) {
    return hours + "\uC2DC\uAC04 " + minutes + "\uBD84 " + seconds + "\uCD08";
  }

  if (minutes > 0) {
    return minutes + "\uBD84 " + seconds + "\uCD08";
  }

  return seconds + "\uCD08";
};

var DescriptiveTimerModule = function DescriptiveTimerModule(_ref2) {
  var _ref2$getDescriptiveT = _ref2.getDescriptiveTimerText,
      getDescriptiveTimerText = _ref2$getDescriptiveT === void 0 ? initialGetDescriptiveTimerText : _ref2$getDescriptiveT,
      end_time = _ref2.end_time,
      background_color = _ref2.background_color,
      complete_title = _ref2.complete_title,
      progress_title = _ref2.progress_title,
      timer_color = _ref2.timer_color,
      title_color = _ref2.title_color,
      frame_style = _ref2.frame_style,
      timer_style = _ref2.timer_style,
      title_style = _ref2.title_style,
      wrapper_style = _ref2.wrapper_style;

  var _useTimer = useTimer({
    end_time: end_time
  }),
      days = _useTimer.days,
      hours = _useTimer.hours,
      minutes = _useTimer.minutes,
      seconds = _useTimer.seconds,
      in_progress = _useTimer.in_progress;

  var title = in_progress ? progress_title : complete_title;
  return /*#__PURE__*/React.createElement("div", {
    className: styles$k.wrapper,
    style: wrapper_style
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$k.frame,
    style: _extends({
      backgroundColor: background_color
    }, frame_style)
  }, title && /*#__PURE__*/React.createElement("div", {
    className: styles$k.title,
    style: _extends({
      color: title_color
    }, title_style)
  }, title), in_progress && /*#__PURE__*/React.createElement("div", {
    className: styles$k.timer,
    style: _extends({
      color: timer_color
    }, timer_style)
  }, getDescriptiveTimerText({
    days: days,
    hours: hours,
    minutes: minutes,
    seconds: seconds
  }))));
};

var styles$j = {
  "button": "Button-module__button___3asLj",
  "link": "Button-module__link___2X7nv"
};
function DialogButton(_ref) {
  var text = _ref.text,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? 'default' : _ref$theme,
      color = _ref.color,
      background_color = _ref.background_color,
      link_url = _ref.link_url,
      onClick = _ref.onClick,
      style = _ref.style;
  return /*#__PURE__*/React.createElement("button", {
    className: styles$j.button,
    onClick: onClick,
    style: _extends({
      color: color || (theme === 'primary' ? '#fff' : '#242424'),
      backgroundColor: background_color || (theme === 'primary' ? '#e62f71' : '#fff'),
      border: "1px solid " + (theme === 'primary' ? '#e62f71' : '#e1e1e1')
    }, style)
  }, link_url ? /*#__PURE__*/React.createElement("a", {
    className: styles$j.link,
    href: link_url
  }, text) : text);
}

var CancelIcon = function CancelIcon() {
  return /*#__PURE__*/React.createElement("svg", {
    width: "1em",
    height: "1em",
    viewBox: "0 0 20 20",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M16 4C15.6 4.4 7.83333 12.1667 4 16",
    stroke: "currentColor",
    strokeWidth: "1.4"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M16 16C15.6 15.6 7.83333 7.83333 4 4",
    stroke: "currentColor",
    strokeWidth: "1.4"
  }));
};

var styles$i = {
  "dialog-wrapper": "Dialog-module__dialog-wrapper___1A1yi",
  "dialog-content": "Dialog-module__dialog-content___3JXXp",
  "close-button-wrapper": "Dialog-module__close-button-wrapper___3Yw3n",
  "close-button": "Dialog-module__close-button___3uP23",
  "header": "Dialog-module__header___zeDnR",
  "icon-wrapper": "Dialog-module__icon-wrapper___207ff",
  "icon-image": "Dialog-module__icon-image___2KtvE",
  "title": "Dialog-module__title___2fwe6",
  "content": "Dialog-module__content___2UeUy",
  "footer": "Dialog-module__footer___2dzWA"
};
var Dialog = function Dialog(_ref) {
  var _ref$visible = _ref.visible,
      visible = _ref$visible === void 0 ? false : _ref$visible,
      closable = _ref.closable,
      icon_src = _ref.icon_src,
      icon_node = _ref.icon_node,
      title = _ref.title,
      content = _ref.content,
      buttons = _ref.buttons,
      onClose = _ref.onClose;
  var has_header = Boolean(icon_node || icon_src || title || content);
  var has_footer = Boolean(buttons == null ? void 0 : buttons.length);
  return /*#__PURE__*/React.createElement("div", {
    className: styles$i['dialog-wrapper'],
    onClick: function onClick() {
      return onClose == null ? void 0 : onClose();
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$i['dialog-content'],
    onClick: function onClick(e) {
      return e.stopPropagation();
    },
    style: {
      transform: "translateY(" + (visible ? '0%' : '100%') + ")"
    }
  }, closable && /*#__PURE__*/React.createElement("div", {
    className: styles$i['close-button-wrapper']
  }, /*#__PURE__*/React.createElement("button", {
    className: styles$i['close-button'],
    onClick: onClose
  }, /*#__PURE__*/React.createElement(CancelIcon, null))), has_header && /*#__PURE__*/React.createElement("div", {
    className: styles$i.header
  }, (icon_node || icon_src) && /*#__PURE__*/React.createElement("div", {
    className: styles$i['icon-wrapper']
  }, icon_node || icon_src && /*#__PURE__*/React.createElement("img", {
    className: styles$i['icon-image'],
    src: icon_src,
    alt: ""
  })), title && /*#__PURE__*/React.createElement("div", {
    className: styles$i.title
  }, title), content && /*#__PURE__*/React.createElement("div", {
    className: styles$i.content
  }, content)), has_footer && /*#__PURE__*/React.createElement("div", {
    className: styles$i.footer
  }, buttons == null ? void 0 : buttons.map(function (props, i) {
    return /*#__PURE__*/React.createElement(DialogButton, _extends({
      key: i
    }, props, {
      style: {
        flex: 1
      }
    }));
  }))));
};

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

var KEY$1 = 'dialog';
var dialog_component = {
  Dialog: Dialog
};
function setDialogComponent(component) {
  dialog_component.Dialog = component;
}
function dialog(_x) {
  return _dialog.apply(this, arguments);
}

function _dialog() {
  _dialog = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee2(options) {
    var container, Dialog, props;
    return regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            container = document.createElement('div');
            document.body.append(container);
            Dialog = dialog_component.Dialog;
            props = _extends({
              key: KEY$1
            }, options);
            ReactDOM.render( /*#__PURE__*/React.createElement(Dialog, props), container);
            _context2.next = 7;
            return halt$1(0);

          case 7:
            _context2.next = 9;
            return new Promise(function (resolve) {
              return ReactDOM.render( /*#__PURE__*/React.createElement(Dialog, _extends({}, props, {
                visible: true,
                onClose: /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
                  var _yield$options$onBefo;

                  return regeneratorRuntime.wrap(function _callee$(_context) {
                    while (1) {
                      switch (_context.prev = _context.next) {
                        case 0:
                          _context.next = 2;
                          return options.onBeforeClose == null ? void 0 : options.onBeforeClose();

                        case 2:
                          _context.t0 = _yield$options$onBefo = _context.sent;

                          if (!(_context.t0 != null)) {
                            _context.next = 7;
                            break;
                          }

                          _context.t1 = _yield$options$onBefo;
                          _context.next = 8;
                          break;

                        case 7:
                          _context.t1 = true;

                        case 8:
                          if (!_context.t1) {
                            _context.next = 10;
                            break;
                          }

                          resolve(null);

                        case 10:
                        case "end":
                          return _context.stop();
                      }
                    }
                  }, _callee);
                }))
              })), container);
            });

          case 9:
            ReactDOM.render( /*#__PURE__*/React.createElement(Dialog, props), container);
            _context2.next = 12;
            return halt$1(300);

          case 12:
            ReactDOM.unmountComponentAtNode(container);
            container.remove();

          case 14:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));
  return _dialog.apply(this, arguments);
}

function halt$1(duration) {
  return new Promise(function (res) {
    return setTimeout(res, duration);
  });
}

var styles$h = {
  "content": "EventLayout-module__content___2tLF8"
};
var EventLayout = function EventLayout(_ref) {
  var children = _ref.children,
      page_background_color = _ref.page_background_color;
  return /*#__PURE__*/React.createElement("div", {
    id: "__content_editor",
    className: styles$h.content,
    style: {
      backgroundColor: page_background_color
    }
  }, children);
};

var FCFSCouponModule = function FCFSCouponModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "FCFSCoupon"
  });
};

var styles$g = {
  "button": "FloatingButton-module__button___2-NRv"
};
var FloatingButtonModule = function FloatingButtonModule(_ref) {
  var action = _ref.action,
      active_background_color = _ref.active_background_color,
      active_color = _ref.active_color,
      button_round = _ref.button_round,
      complete_text = _ref.complete_text,
      disabled_background_color = _ref.disabled_background_color,
      disabled_color = _ref.disabled_color,
      font_weight = _ref.font_weight,
      prepare_text = _ref.prepare_text,
      progress_text = _ref.progress_text;

  var _useContentMetadata = useContentMetadata(),
      is_after = _useContentMetadata.is_after,
      is_before = _useContentMetadata.is_before;

  var _useAction = useAction(action),
      dispatch = _useAction.dispatch,
      state = _useAction.state;

  var disabled = is_after || is_before || state.disabled || state.dispatching;
  return /*#__PURE__*/React.createElement("button", {
    disabled: disabled,
    className: styles$g.button,
    style: {
      borderRadius: button_round,
      fontWeight: font_weight === 'bold' ? 700 : 400,
      color: disabled ? disabled_color : active_color,
      backgroundColor: disabled ? disabled_background_color : active_background_color,
      cursor: disabled ? 'not-allowed' : 'pointer',
      zIndex: 999
    },
    onClick: dispatch
  }, is_before ? prepare_text : is_after ? complete_text : progress_text);
};

var ImageCarouselModule = function ImageCarouselModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "Carousel"
  });
};

var ResizeEventListenerContext = /*#__PURE__*/createContext({
  addResizeEventListener: function addResizeEventListener() {},
  removeResizeEventListener: function removeResizeEventListener() {}
});
ResizeEventListenerContext.displayName = 'ResizeEventListenerContext';
function ResizeEventListenerContextProvider(_ref) {
  var children = _ref.children;
  var resizeEventListenerSet_ref = useRef(new Set());

  var addResizeEventListener = function addResizeEventListener(listener) {
    return resizeEventListenerSet_ref.current.add(listener);
  };

  var removeResizeEventListener = function removeResizeEventListener(listener) {
    return resizeEventListenerSet_ref.current["delete"](listener);
  };

  useEffect(function () {
    var handleResizeViaListenerSet = function handleResizeViaListenerSet(event) {
      resizeEventListenerSet_ref.current.forEach(function (listener) {
        return listener.call(window, event);
      });
    };

    addEventListener('resize', handleResizeViaListenerSet);
    return function () {
      return removeEventListener('resize', handleResizeViaListenerSet);
    };
  }, []);
  return /*#__PURE__*/React.createElement(ResizeEventListenerContext.Provider, {
    value: {
      addResizeEventListener: addResizeEventListener,
      removeResizeEventListener: removeResizeEventListener
    }
  }, children);
}
function useResizeEventListener(_ref2) {
  var onResize = _ref2.onResize,
      onLoad = _ref2.onLoad,
      _ref2$deps = _ref2.deps,
      deps = _ref2$deps === void 0 ? [] : _ref2$deps;
  var onResize_ref = useRef(onResize);
  onResize_ref.current = onResize;
  var onLoad_ref = useRef(onLoad);
  onLoad_ref.current = onLoad;

  var _useContext = useContext(ResizeEventListenerContext),
      addResizeEventListener = _useContext.addResizeEventListener,
      removeResizeEventListener = _useContext.removeResizeEventListener;

  useEffect(function () {
    var latestOnResize = function latestOnResize(event) {
      return onResize_ref.current.call(window, event);
    };

    var latestOnLoad = function latestOnLoad() {
      return onLoad_ref.current == null ? void 0 : onLoad_ref.current();
    };

    latestOnLoad();
    addResizeEventListener(latestOnResize);
    return function () {
      return removeResizeEventListener(latestOnResize);
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function useFlexibleSize(_ref) {
  var ref = _ref.ref,
      base_width = _ref.base_width,
      _ref$enabled = _ref.enabled,
      enabled = _ref$enabled === void 0 ? true : _ref$enabled;

  var _useState = useState(false),
      is_inited = _useState[0],
      setIsInited = _useState[1];

  var _useState2 = useState(0),
      target_width = _useState2[0],
      setTargetWidth = _useState2[1];

  var scale = target_width / base_width;
  useResizeEventListener({
    onResize: refreshTargetWidth,
    onLoad: refreshTargetWidth,
    deps: [enabled]
  });
  return {
    scale: scale,
    is_inited: is_inited
  };

  function refreshTargetWidth() {
    var _ref$current$clientWi, _ref$current;

    if (!enabled) {
      return;
    }

    setIsInited(true);
    setTargetWidth((_ref$current$clientWi = (_ref$current = ref.current) == null ? void 0 : _ref$current.clientWidth) != null ? _ref$current$clientWi : 0);
  }
}

var styles$f = {
  "wrapper": "SingleImage-module__wrapper___3cjPS",
  "area": "SingleImage-module__area___1gFV9",
  "image": "SingleImage-module__image___1BNLN"
};
var SingleImage = function SingleImage(_ref) {
  var _ref$data_source = _ref.data_source,
      src = _ref$data_source.src,
      _ref$data_source$acti = _ref$data_source.action_map,
      action_map = _ref$data_source$acti === void 0 ? {} : _ref$data_source$acti,
      _ref$data_source$area = _ref$data_source.area_map,
      area_map = _ref$data_source$area === void 0 ? {} : _ref$data_source$area,
      width = _ref$data_source.width,
      height = _ref$data_source.height,
      wrapper_style = _ref.wrapper_style,
      img_style = _ref.img_style,
      _ref$enabled = _ref.enabled,
      enabled = _ref$enabled === void 0 ? true : _ref$enabled,
      _ref$draggable = _ref.draggable,
      draggable = _ref$draggable === void 0 ? false : _ref$draggable;
  var ref = useRef(null);

  var _useFlexibleSize = useFlexibleSize({
    ref: ref,
    base_width: 500,
    enabled: enabled
  }),
      scale = _useFlexibleSize.scale;

  var is_use_map = Boolean(Object.keys(area_map).length);
  var height_ratio = Math.floor(height / width * 1000) / 10;
  var with_aspect_ratio = !isNaN(height_ratio);
  return /*#__PURE__*/React.createElement("div", {
    className: styles$f.wrapper,
    style: _extends({}, with_aspect_ratio && {
      paddingBottom: height_ratio + '%'
    }, wrapper_style)
  }, enabled && is_use_map && /*#__PURE__*/React.createElement("div", {
    className: styles$f.area
  }, Object.keys(area_map).map(function (area_id) {
    return /*#__PURE__*/React.createElement(ImageArea, {
      key: area_id,
      area: area_map[area_id],
      action: action_map[area_id],
      image_size_correction_ratio: scale
    });
  })), enabled && /*#__PURE__*/React.createElement("img", {
    className: styles$f.image,
    src: src,
    alt: "",
    draggable: draggable,
    ref: ref,
    style: _extends({}, with_aspect_ratio && {
      position: 'absolute',
      top: -0.4,
      left: -0.4
    }, img_style)
  }));
};

var ImageMapModule = function ImageMapModule(props) {
  return /*#__PURE__*/React.createElement(React.Fragment, null, props.image_list.map(function (image) {
    return /*#__PURE__*/React.createElement(SingleImage, {
      key: image.id,
      data_source: image
    });
  }));
};

var ImageRailModule = function ImageRailModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "ImageRail"
  });
};

var ImageSlideModule = function ImageSlideModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "ImageSlide"
  });
};

var ProductCarouselModule = function ProductCarouselModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "ProductCarouselModuleProps"
  });
};

var ProductFilterModule = function ProductFilterModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "ProductFilter"
  });
};

var ProductListModule = function ProductListModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "ProductList"
  });
};

var ProductTabModule = function ProductTabModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "ProductTab"
  });
};

var ProductRailModule = function ProductRailModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "ProductRail"
  });
};

var LayoutVariablesContextDefaultValue = {
  container_max_width: 720,
  global_header_height: 0,
  tab_area_tab_header_height: 0,
  product_tab_header_height: 0,
  getSafeAreaVariablesFromElement: function getSafeAreaVariablesFromElement() {
    var _document$getElementB;

    return (_document$getElementB = document.getElementById('__content_editor')) != null ? _document$getElementB : document.body;
  }
};
var LayoutVariablesContext = /*#__PURE__*/React.createContext(LayoutVariablesContextDefaultValue);
LayoutVariablesContext.displayName = 'LayoutVariablesContext';
function useLayoutVariables() {
  return useContext(LayoutVariablesContext);
}
function LayoutVariablesProvider(_ref) {
  var value = _ref.value,
      children = _ref.children;
  return /*#__PURE__*/React.createElement(LayoutVariablesContext.Provider, {
    value: _extends({}, LayoutVariablesContextDefaultValue, value)
  }, children);
}

var ContainerWidthContext = /*#__PURE__*/React.createContext(720);
ContainerWidthContext.displayName = 'ContainerWidthContext';
function ContainerWidthProvider(_ref) {
  var children = _ref.children;
  var container_width = useContainerWidthResizeObserver();
  return /*#__PURE__*/React.createElement(ContainerWidthContext.Provider, {
    value: container_width
  }, children);
}

function useContainerWidthResizeObserver() {
  var _useLayoutVariables = useLayoutVariables(),
      container_max_width = _useLayoutVariables.container_max_width;

  var _useState = useState(container_max_width),
      container_width = _useState[0],
      setContainerWidth = _useState[1];

  useResizeEventListener({
    onResize: resetContainerWidth,
    onLoad: resetContainerWidth
  });
  return container_width;

  function resetContainerWidth() {
    var min_width = Math.min(container_max_width, document.body.clientWidth);
    setContainerWidth(min_width);
  }
}

function useContainerWidth() {
  return React.useContext(ContainerWidthContext);
}

function useIsFixedImageModule() {
  var _useModuleHierarchy = useModuleHierarchy(),
      modules = _useModuleHierarchy.modules;

  var current_module = modules[modules.length - 1];
  var is_fixed_top = isImageModule(current_module) && Boolean(current_module.fixed_top);
  return is_fixed_top;
}
function useFixedImageHeight() {
  var component_map = useComponentMap({
    use_visible_between_option: true
  });
  var container_width = useContainerWidth();
  var fixed_image_module_uuid = Object.keys(component_map).find(function (uuid) {
    var current_module = component_map[uuid];
    var is_fixed_top = isImageModule(current_module) && Boolean(current_module.fixed_top);
    return is_fixed_top;
  });

  if (!fixed_image_module_uuid) {
    return 0;
  }

  var fixed_image_module = component_map[fixed_image_module_uuid];

  if (isMaxImageAspectRatioModule(fixed_image_module)) {
    var image_aspect_ratio = maxImageAspectRatio(fixed_image_module.image_list);
    var image_height = image_aspect_ratio / 100 * container_width;
    return Math.floor(image_height);
  }

  if (isSumImageAspectRatioModule(fixed_image_module)) {
    var _image_aspect_ratio = sumImageAspectRatio(fixed_image_module.image_list);

    var _image_height = _image_aspect_ratio / 100 * container_width;

    return Math.floor(_image_height);
  }

  return 0;
}
function maxImageAspectRatio(image_list) {
  return image_list.reduce(function (acc, _ref) {
    var _ref$width = _ref.width,
        width = _ref$width === void 0 ? 100 : _ref$width,
        _ref$height = _ref.height,
        height = _ref$height === void 0 ? 100 : _ref$height;
    var aspect_ratio = Math.floor(height / width * 1000) / 10;
    return acc > aspect_ratio ? acc : aspect_ratio;
  }, 0);
}
function sumImageAspectRatio(image_list) {
  return image_list.reduce(function (acc, _ref2) {
    var _ref2$width = _ref2.width,
        width = _ref2$width === void 0 ? 100 : _ref2$width,
        _ref2$height = _ref2.height,
        height = _ref2$height === void 0 ? 100 : _ref2$height;
    var aspect_ratio = Math.floor(height / width * 1000) / 10;
    return acc + aspect_ratio;
  }, 0);
}

function initializeModuleLandingScroll(_ref) {
  var uuid = _ref.uuid,
      offset_top = _ref.offset_top;
  var module_landing_hash = window.location.hash.replace('#', '');
  var module_landing_target_element = document.getElementById(module_landing_hash);
  var should_landing_by_current_element = uuid === module_landing_hash;

  if (module_landing_target_element && should_landing_by_current_element) {
    window.scrollBy(0, module_landing_target_element.getBoundingClientRect().y - offset_top);
  }
}

function useSafeArea() {
  var _React$useState = React.useState({
    inset_top: 0,
    inset_right: 0,
    inset_bottom: 0,
    inset_left: 0
  }),
      safe_area = _React$useState[0],
      setSafeArea = _React$useState[1];

  var _useLayoutVariables = useLayoutVariables(),
      getSafeAreaVariablesFromElement = _useLayoutVariables.getSafeAreaVariablesFromElement;

  var getSafeAreaVariablesFromElementRef = React.useRef(getSafeAreaVariablesFromElement);
  useEffect(function () {
    var root_element = getSafeAreaVariablesFromElementRef.current(); // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition

    if (root_element) {
      var safe_area_inset_top_value = getComputedStyle(root_element).getPropertyValue('--safe-area-inset-top');
      var safe_area_inset_right_value = getComputedStyle(root_element).getPropertyValue('--safe-area-inset-right');
      var safe_area_inset_bottom_value = getComputedStyle(root_element).getPropertyValue('--safe-area-inset-bottom');
      var safe_area_inset_left_value = getComputedStyle(root_element).getPropertyValue('--safe-area-inset-left');
      setSafeArea({
        inset_top: parseNumericValue(safe_area_inset_top_value),
        inset_right: parseNumericValue(safe_area_inset_right_value),
        inset_bottom: parseNumericValue(safe_area_inset_bottom_value),
        inset_left: parseNumericValue(safe_area_inset_left_value)
      });
    }
  }, []);
  return safe_area;
}

function parseNumericValue(str) {
  var match_array = str.trim().match(/([-+]?[0-9]*\.?[0-9]*)/);
  var arabic_string = match_array ? match_array[0] : '0';
  var numeric_value = parseInt(arabic_string);
  return isNaN(numeric_value) ? 0 : numeric_value;
}

function useTabLayout() {
  var layoutVariables = useLayoutVariables();

  var _useModuleHierarchy = useModuleHierarchy(),
      modules = _useModuleHierarchy.modules;

  var safeAreaInsetTop = useSafeArea().inset_top;
  var offsetTopWithFixedImage = useOffsetTopWithFixedImage(modules);
  var offsetTopWithTabModule = useOffsetTopWithTabModule(modules, layoutVariables);
  var currentTabButtonHeightDetermineTabContentShift = useCurrentTabButtonHeightDetermineTabContentShift(modules, layoutVariables);
  var inheritOffsetTop = safeAreaInsetTop + offsetTopWithFixedImage + offsetTopWithTabModule;
  var tab_header_top = layoutVariables.global_header_height + inheritOffsetTop;
  var waypoint_top_offset = tab_header_top + currentTabButtonHeightDetermineTabContentShift;
  var scroll_target_subtract = waypoint_top_offset - 1;

  var scrollToTarget = function scrollToTarget(target, scroll_options) {
    if (!target) {
      return;
    }

    var element_position = target.getBoundingClientRect().top;
    var offset_position = element_position - scroll_target_subtract;
    window.scrollBy(_extends({
      top: offset_position
    }, scroll_options));
  };

  return {
    tab_header_top: tab_header_top,
    waypoint_top_offset: waypoint_top_offset,
    scrollToTarget: scrollToTarget
  };
}

function useOffsetTopWithFixedImage(modules) {
  var current_module = modules[modules.length - 1];
  var fixedImageHeight = useFixedImageHeight();
  var isFixedImageModule = isImageModule(current_module) && current_module.fixed_top;

  if (isFixedImageModule) {
    return 0;
  }

  return fixedImageHeight;
}

function useOffsetTopWithTabModule(modules, _ref) {
  var tab_area_tab_header_height = _ref.tab_area_tab_header_height;
  var ancestorModules = modules.slice(0, -1);
  var currentModule = modules[modules.length - 1];
  var hasTabAreaAncestor = getHasTabAreaAncestor(ancestorModules);
  var isTabModuleItself = isTabModule(currentModule);

  if (isTabModuleItself) {
    return 0;
  }

  if (hasTabAreaAncestor) {
    return tab_area_tab_header_height;
  }

  return 0;
}

function useCurrentTabButtonHeightDetermineTabContentShift(modules, _ref2) {
  var tab_area_tab_header_height = _ref2.tab_area_tab_header_height,
      product_tab_header_height = _ref2.product_tab_header_height;
  var currentModule = modules[modules.length - 1];
  var isTabModuleItself = isTabModule(currentModule);
  var isProductTabModuleItself = isProductTabModule(currentModule);

  if (isTabModuleItself) {
    return tab_area_tab_header_height;
  }

  if (isProductTabModuleItself) {
    return product_tab_header_height;
  }

  return 0;
}

function getHasTabAreaAncestor(ancestor_modules) {
  return ancestor_modules.filter(isTabModule).length > 0;
}

var useViewSizeBaseOnDeviceWidth = function useViewSizeBaseOnDeviceWidth(max_content_width) {
  return function (size) {
    if (size > 0) {
      return "min(" + size / 375 * 100 + "vw, " + max_content_width * size / 375 + "px)";
    } else {
      return "max(" + size / 375 * 100 + "vw, " + max_content_width * size / 375 + "px)";
    }
  };
};

var ProgressiveImage = function ProgressiveImage(_ref) {
  var data_source = _ref.data_source,
      img_style = _ref.img_style,
      wrapper_style = _ref.wrapper_style,
      _ref$enabled = _ref.enabled,
      enabled = _ref$enabled === void 0 ? true : _ref$enabled;
  var image = useLazyLoadedOriginalImage({
    enabled: enabled,
    lowQualityImage: getLowQualityImage$1(data_source),
    middleQualityImage: getMiddleQualityImage$1(data_source),
    originalImage: data_source
  });
  return /*#__PURE__*/React.createElement(SingleImage, {
    enabled: enabled,
    data_source: image,
    img_style: img_style,
    wrapper_style: wrapper_style
  });
};

function getLowQualityImage$1(image) {
  try {
    var src = image.src,
        width = image.width,
        height = image.height;
    var imgUrl = new URL(src);
    var searchParams = imgUrl.searchParams;
    searchParams.set('width', String(Math.floor(width / 10)));
    searchParams.set('height', String(Math.floor(height / 10)));
    searchParams.set('quality', '10');
    searchParams.set('format', 'webp');
    return _extends({}, image, {
      src: imgUrl.toString()
    });
  } catch (error) {
    return image;
  }
}

function getMiddleQualityImage$1(image) {
  try {
    var src = image.src,
        width = image.width,
        height = image.height;
    var imgUrl = new URL(src);
    var searchParams = imgUrl.searchParams;
    searchParams.set('width', String(Math.floor(width / 2)));
    searchParams.set('height', String(Math.floor(height / 2)));
    searchParams.set('quality', '50');
    searchParams.set('format', 'webp');
    return _extends({}, image, {
      src: imgUrl.toString()
    });
  } catch (error) {
    return image;
  }
}

function useLazyLoadedOriginalImage(_ref2) {
  var lowQualityImage = _ref2.lowQualityImage,
      middleQualityImage = _ref2.middleQualityImage,
      originalImage = _ref2.originalImage,
      enabled = _ref2.enabled;
  var originalImageLoadedRef = useRef(false);

  var _useState = useState(lowQualityImage),
      displayingImage = _useState[0],
      setDisplayingImage = _useState[1];

  useEffect(function () {
    if (enabled) {
      try {
        preloadMiddleQualityImageIfOriginalImageNotLoaded();
        preloadOriginalImageAndMarkMiddleQualityImageAsNotNeeded();
      } catch (error) {
        accidentlyShowOriginalImage();
      }
    }

    function preloadOriginalImageAndMarkMiddleQualityImageAsNotNeeded() {
      var preloadOriginalImage = new Image();

      preloadOriginalImage.onload = function () {
        originalImageLoadedRef.current = true;
        setDisplayingImage(originalImage);
      };

      preloadOriginalImage.src = originalImage.src;
    }

    function preloadMiddleQualityImageIfOriginalImageNotLoaded() {
      var preloadMiddleQualityImage = new Image();

      preloadMiddleQualityImage.onload = function () {
        if (!originalImageLoadedRef.current) {
          setDisplayingImage(middleQualityImage);
        }
      };

      preloadMiddleQualityImage.src = middleQualityImage.src;
    }

    function accidentlyShowOriginalImage() {
      setDisplayingImage(originalImage);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [enabled]);
  return displayingImage;
}

var styles$e = {
  "artwork": "QuickArtworkTypeA-module__artwork___1SDCZ",
  "artwork-background": "QuickArtworkTypeA-module__artwork-background___1pZ8u",
  "artwork-dimmed": "QuickArtworkTypeA-module__artwork-dimmed___PTkPf",
  "artwork-layout": "QuickArtworkTypeA-module__artwork-layout___1w__X",
  "artwork-description": "QuickArtworkTypeA-module__artwork-description___3kQpH",
  "artwork-title": "QuickArtworkTypeA-module__artwork-title___7SOe7",
  "artwork-subtitle": "QuickArtworkTypeA-module__artwork-subtitle___1ESwY",
  "artwork-date": "QuickArtworkTypeA-module__artwork-date___eA439",
  "artwork-badge-list": "QuickArtworkTypeA-module__artwork-badge-list____Z769",
  "artwork-badge-item": "QuickArtworkTypeA-module__artwork-badge-item___2eBhe"
};
/**
 * @typedef {Object} QuickArtworkTypeAModuleProps
 * @property {string} date - 단순 디스플레이용 날짜 문자열
 * @property {number} start_date - BO에서 편집할 때 사용하는 날짜 raw data;
 * @property {number} end_date - BO에서 편집할 때 사용하는 날짜 raw data;
 */

function QuickArtworkTypeAModule(_ref) {
  var title1 = _ref.title1,
      title2 = _ref.title2,
      subtitle1 = _ref.subtitle1,
      subtitle2 = _ref.subtitle2,
      backgroundSrc = _ref.backgroundSrc,
      date = _ref.date,
      _ref$badges = _ref.badges,
      badges = _ref$badges === void 0 ? [] : _ref$badges,
      _ref$opacity = _ref.opacity,
      opacity = _ref$opacity === void 0 ? 0 : _ref$opacity,
      _ref$isOnIntersection = _ref.isOnIntersection,
      isOnIntersection = _ref$isOnIntersection === void 0 ? true : _ref$isOnIntersection;

  var _useLayoutVariables = useLayoutVariables(),
      container_max_width = _useLayoutVariables.container_max_width;

  var vw = useViewSizeBaseOnDeviceWidth(container_max_width);
  return /*#__PURE__*/React.createElement("div", {
    className: styles$e.artwork
  }, /*#__PURE__*/React.createElement(ProgressiveImage, {
    data_source: {
      id: backgroundSrc,
      src: backgroundSrc,
      area_map: {},
      action_map: {},
      width: 375,
      height: 600
    },
    enabled: isOnIntersection,
    img_style: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    wrapper_style: {
      position: 'relative',
      paddingBottom: 100 / (375 / 600) + "%"
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: styles$e['artwork-dimmed'],
    style: {
      opacity: opacity
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: styles$e['artwork-layout'],
    style: {
      padding: vw(15)
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$e['artwork-description']
  }, /*#__PURE__*/React.createElement("h2", {
    className: styles$e['artwork-title'],
    style: {
      paddingTop: vw(50),
      fontSize: vw(28),
      lineHeight: vw(39.2)
    }
  }, title1), title2 && /*#__PURE__*/React.createElement("h2", {
    className: styles$e['artwork-title'],
    style: {
      fontSize: vw(28),
      lineHeight: vw(39.2)
    }
  }, title2), /*#__PURE__*/React.createElement("h4", {
    className: styles$e['artwork-subtitle'],
    style: {
      paddingTop: vw(8),
      fontSize: vw(16),
      lineHeight: vw(22.4)
    }
  }, subtitle1), subtitle2 && /*#__PURE__*/React.createElement("h4", {
    className: styles$e['artwork-subtitle'],
    style: {
      fontSize: vw(16),
      lineHeight: vw(22.4)
    }
  }, subtitle2)), /*#__PURE__*/React.createElement("div", {
    style: {
      paddingBottom: vw(42)
    }
  }, /*#__PURE__*/React.createElement("p", {
    className: styles$e['artwork-date'],
    style: {
      fontSize: vw(13),
      lineHeight: vw(15.6)
    }
  }, date), /*#__PURE__*/React.createElement("div", {
    className: styles$e['artwork-badge-list'],
    style: {
      paddingTop: vw(24),
      gap: vw(8)
    }
  }, badges.map(function (badge) {
    return /*#__PURE__*/React.createElement("div", {
      key: badge,
      className: styles$e['artwork-badge-item'],
      style: {
        height: vw(28),
        fontSize: vw(13),
        lineHeight: vw(15.6),
        letterSpacing: vw(-0.6),
        padding: vw(6) + " " + vw(10),
        borderRadius: vw(6)
      }
    }, badge);
  })))));
}

var styles$d = {
  "artwork": "QuickArtworkTypeB-module__artwork___Umblw",
  "artwork-layout": "QuickArtworkTypeB-module__artwork-layout___3gaUX",
  "artwork-title": "QuickArtworkTypeB-module__artwork-title___2sbM8",
  "artwork-subtitle": "QuickArtworkTypeB-module__artwork-subtitle___2F7Uz",
  "artwork-date": "QuickArtworkTypeB-module__artwork-date___1d-Ev",
  "artwork-coupon": "QuickArtworkTypeB-module__artwork-coupon___2x_KZ",
  "artwork-coupon-image": "QuickArtworkTypeB-module__artwork-coupon-image___2s87N",
  "artwork-coupon-name": "QuickArtworkTypeB-module__artwork-coupon-name___36jOP",
  "coupon-guide-texts": "QuickArtworkTypeB-module__coupon-guide-texts___2toE8"
};
/**
 * @typedef {Object} QuickArtworkTypeAModuleProps
 * @property {string} date - 단순 디스플레이용 날짜 문자열
 * @property {number} start_date - BO에서 편집할 때 사용하는 날짜 raw data;
 * @property {number} end_date - BO에서 편집할 때 사용하는 날짜 raw data;
 */

function QuickArtworkTypeBModule(_ref) {
  var title1 = _ref.title1,
      title2 = _ref.title2,
      subtitle1 = _ref.subtitle1,
      subtitle2 = _ref.subtitle2,
      date = _ref.date,
      couponName = _ref.couponName,
      couponColor = _ref.couponColor,
      action = _ref.action,
      guide_texts = _ref.guide_texts;

  var _useLayoutVariables = useLayoutVariables(),
      container_max_width = _useLayoutVariables.container_max_width;

  var vw = useViewSizeBaseOnDeviceWidth(container_max_width);

  var _useAction = useAction(action != null ? action : null),
      dispatch = _useAction.dispatch;

  var handleAction = function handleAction() {
    if (action) {
      dispatch();
    }
  };

  return /*#__PURE__*/React.createElement("div", {
    className: styles$d.artwork
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$d['artwork-layout'],
    style: {
      padding: vw(56) + " " + vw(16) + " " + vw(50) + " " + vw(16)
    }
  }, /*#__PURE__*/React.createElement("p", {
    className: styles$d['artwork-date'],
    style: {
      fontSize: vw(14),
      lineHeight: vw(19.6)
    }
  }, date), /*#__PURE__*/React.createElement("h2", {
    className: styles$d['artwork-title'],
    style: {
      paddingTop: vw(8),
      fontSize: vw(24),
      lineHeight: vw(33.6),
      letterSpacing: vw(-0.22)
    }
  }, title1), title2 && /*#__PURE__*/React.createElement("h2", {
    className: styles$d['artwork-title'],
    style: {
      fontSize: vw(24),
      lineHeight: vw(33.6),
      letterSpacing: vw(-0.22)
    }
  }, title2), /*#__PURE__*/React.createElement("h4", {
    className: styles$d['artwork-subtitle'],
    style: {
      paddingTop: vw(8),
      fontSize: vw(16),
      lineHeight: vw(22.4),
      letterSpacing: vw(-0.2)
    }
  }, subtitle1), subtitle2 && /*#__PURE__*/React.createElement("h4", {
    className: styles$d['artwork-subtitle'],
    style: {
      fontSize: vw(16),
      lineHeight: vw(22.4),
      letterSpacing: vw(-0.2)
    }
  }, subtitle2), /*#__PURE__*/React.createElement("div", {
    className: styles$d['artwork-coupon'],
    style: {
      marginTop: vw(24)
    },
    onClick: handleAction
  }, /*#__PURE__*/React.createElement("svg", {
    className: styles$d['artwork-coupon-image'],
    viewBox: "0 0 343 50",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M0 0C104.617 0 182.883 0 287.5 0L292.5 5.1021L298 0C298 0 329.715 0 343 0V50H298L292.5 44.898L287.5 50H0V0Z",
    fill: couponColor
  }), /*#__PURE__*/React.createElement("g", {
    opacity: "0.3"
  }, /*#__PURE__*/React.createElement("rect", {
    x: "292",
    y: "15",
    width: "2",
    height: "2",
    fill: "white"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "292",
    y: "21",
    width: "2",
    height: "2",
    fill: "white"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "292",
    y: "27",
    width: "2",
    height: "2",
    fill: "white"
  }), /*#__PURE__*/React.createElement("rect", {
    x: "292",
    y: "33",
    width: "2",
    height: "2",
    fill: "white"
  })), /*#__PURE__*/React.createElement("path", {
    d: "M324.687 22.5938L319 28.2813L313.312 22.5938",
    stroke: "white"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M319 28.2813L319 15.6875",
    stroke: "white",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M311.688 30.7188H326.312",
    stroke: "white",
    strokeLinejoin: "round"
  })), /*#__PURE__*/React.createElement("p", {
    className: styles$d['artwork-coupon-name'],
    style: {
      left: vw(21),
      top: '50%',
      fontSize: vw(15),
      lineHeight: vw(20),
      transform: "translateY(-50%)",
      letterSpacing: vw(-0.1)
    }
  }, couponName)), guide_texts && guide_texts.length > 0 && /*#__PURE__*/React.createElement("ul", {
    className: styles$d['coupon-guide-texts'],
    style: {
      paddingTop: vw(24),
      paddingLeft: vw(18)
    }
  }, guide_texts.map(function (guide) {
    return /*#__PURE__*/React.createElement("li", {
      key: guide,
      style: {
        fontSize: vw(11),
        lineHeight: vw(15.4),
        letterSpacing: vw(-0.1)
      }
    }, guide);
  }))));
}

var styles$c = {
  "banner": "QuickBannerTypeA-module__banner___2Sruz",
  "banner-layout": "QuickBannerTypeA-module__banner-layout___-9w4F",
  "banner-title": "QuickBannerTypeA-module__banner-title___WRqg1",
  "banner-subtitle": "QuickBannerTypeA-module__banner-subtitle___39zHU"
};
function QuickBannerTypeAModule(_ref) {
  var title1 = _ref.title1,
      title2 = _ref.title2,
      subtitle1 = _ref.subtitle1,
      subtitle2 = _ref.subtitle2,
      _ref$background = _ref.background,
      background = _ref$background === void 0 ? '#ffffff' : _ref$background,
      _ref$title1_color = _ref.title1_color,
      title1_color = _ref$title1_color === void 0 ? '#242729' : _ref$title1_color,
      _ref$title2_color = _ref.title2_color,
      title2_color = _ref$title2_color === void 0 ? '#242729' : _ref$title2_color,
      _ref$subtitle1_color = _ref.subtitle1_color,
      subtitle1_color = _ref$subtitle1_color === void 0 ? '#A1A9AD' : _ref$subtitle1_color,
      _ref$subtitle2_color = _ref.subtitle2_color,
      subtitle2_color = _ref$subtitle2_color === void 0 ? '#A1A9AD' : _ref$subtitle2_color;

  var _useLayoutVariables = useLayoutVariables(),
      container_max_width = _useLayoutVariables.container_max_width;

  var vw = useViewSizeBaseOnDeviceWidth(container_max_width);
  return /*#__PURE__*/React.createElement("div", {
    className: styles$c.banner,
    style: {
      background: background
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$c['banner-layout'],
    style: {
      padding: vw(24) + " " + vw(16)
    }
  }, /*#__PURE__*/React.createElement("h2", {
    className: styles$c['banner-title'],
    style: {
      fontSize: vw(24),
      lineHeight: vw(33.6),
      letterSpacing: vw(-0.1),
      color: title1_color
    }
  }, title1), title2 && /*#__PURE__*/React.createElement("h2", {
    className: styles$c['banner-title'],
    style: {
      fontSize: vw(24),
      lineHeight: vw(33.6),
      letterSpacing: vw(-0.1),
      color: title2_color
    }
  }, title2), /*#__PURE__*/React.createElement("h3", {
    className: styles$c['banner-subtitle'],
    style: {
      paddingTop: vw(8),
      fontSize: vw(16),
      lineHeight: vw(22.4),
      letterSpacing: vw(-0.1),
      color: subtitle1_color
    }
  }, subtitle1), subtitle2 && /*#__PURE__*/React.createElement("h3", {
    className: styles$c['banner-subtitle'],
    style: {
      fontSize: vw(16),
      lineHeight: vw(22),
      letterSpacing: vw(-0.1),
      color: subtitle2_color
    }
  }, subtitle2)));
}

var styles$b = {
  "banner": "QuickBannerTypeB-module__banner___zLiVo",
  "banner-contents": "QuickBannerTypeB-module__banner-contents___34jx6",
  "banner-title": "QuickBannerTypeB-module__banner-title___1UaP-",
  "banner-subtitle": "QuickBannerTypeB-module__banner-subtitle___2Qz9e"
};
function QuickBannerTypeBModule(_ref) {
  var title = _ref.title,
      subtitle = _ref.subtitle,
      background = _ref.background;

  var _useLayoutVariables = useLayoutVariables(),
      container_max_width = _useLayoutVariables.container_max_width;

  var vw = useViewSizeBaseOnDeviceWidth(container_max_width);
  return /*#__PURE__*/React.createElement("div", {
    className: styles$b.banner,
    style: {
      padding: vw(20) + " " + vw(16) + " 0 " + vw(16)
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$b['banner-contents'],
    style: {
      background: background,
      height: vw(70),
      borderRadius: vw(12) + " " + vw(12) + " 0 0"
    }
  }, /*#__PURE__*/React.createElement("h3", {
    className: styles$b['banner-title'],
    style: {
      fontSize: vw(18),
      lineHeight: vw(21.6)
    }
  }, title), /*#__PURE__*/React.createElement("h4", {
    className: styles$b['banner-subtitle'],
    style: {
      paddingTop: vw(4),
      fontSize: vw(13),
      lineHeight: vw(15.6)
    }
  }, subtitle)));
}

var styles$a = {
  "coupon": "QuickBenefitTypeA-module__coupon___16q4x",
  "dark": "QuickBenefitTypeA-module__dark___2BrdK",
  "coupon-title": "QuickBenefitTypeA-module__coupon-title___3ymlb",
  "coupon-interaction": "QuickBenefitTypeA-module__coupon-interaction___3iOZ4",
  "coupon-interaction-image": "QuickBenefitTypeA-module__coupon-interaction-image___1Nb07",
  "coupon-interaction-content": "QuickBenefitTypeA-module__coupon-interaction-content___3X9Ku",
  "coupon-interaction-subtitle": "QuickBenefitTypeA-module__coupon-interaction-subtitle___1rreZ",
  "coupon-interaction-title": "QuickBenefitTypeA-module__coupon-interaction-title___3RuKK",
  "coupon-guide-texts": "QuickBenefitTypeA-module__coupon-guide-texts___2chnQ"
};
var QuickBenefitTypeAModule = function QuickBenefitTypeAModule(_ref) {
  var title = _ref.title,
      coupon_subtitle = _ref.coupon_subtitle,
      coupon_title = _ref.coupon_title,
      guide_texts = _ref.guide_texts,
      theme = _ref.theme,
      action = _ref.action;

  var _useLayoutVariables = useLayoutVariables(),
      container_max_width = _useLayoutVariables.container_max_width;

  var vw = useViewSizeBaseOnDeviceWidth(container_max_width);

  var _useAction = useAction(action != null ? action : null),
      dispatch = _useAction.dispatch;

  var isDarkTheme = theme === 'dark';

  var handleAction = function handleAction() {
    if (action) {
      dispatch();
    }
  };

  var withThemeClasses = function withThemeClasses(class_name) {
    return class_name + (isDarkTheme ? ' ' + styles$a.dark : '');
  };

  return /*#__PURE__*/React.createElement("div", {
    className: withThemeClasses(styles$a.coupon),
    style: {
      padding: vw(50) + " " + vw(16)
    }
  }, /*#__PURE__*/React.createElement("h2", {
    className: withThemeClasses(styles$a['coupon-title']),
    style: {
      paddingTop: vw(8),
      fontSize: vw(24),
      lineHeight: vw(28.8)
    }
  }, title), /*#__PURE__*/React.createElement("div", {
    className: withThemeClasses(styles$a['coupon-interaction']),
    style: {
      marginTop: vw(16)
    },
    onClick: handleAction
  }, /*#__PURE__*/React.createElement("svg", {
    className: withThemeClasses(styles$a['coupon-interaction-image']),
    width: "343",
    height: "110",
    viewBox: "0 0 343 110",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    fillRule: "evenodd",
    clipRule: "evenodd",
    d: "M0 110V100V10V0H10H40.265H258.433C263.956 0 268.433 4.47715 268.433 10V100C268.433 105.523 263.956 110 258.433 110H40.265H9.99999H0ZM278.435 0C272.912 0 268.435 4.47715 268.435 10V100C268.435 105.523 272.912 110 278.435 110H302.735H333H343V0H333H302.735H278.435Z",
    fill: isDarkTheme ? '#F1ECEA' : '#000000'
  }), /*#__PURE__*/React.createElement("path", {
    d: "M313 54.5L306 61.5L299 54.5",
    stroke: isDarkTheme ? '#242729' : '#FAFAFB',
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M306 61.5L306 46",
    stroke: isDarkTheme ? '#242729' : '#FAFAFB',
    strokeWidth: "1.5",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M297 64.5H315",
    stroke: isDarkTheme ? '#242729' : '#FAFAFB',
    strokeWidth: "1.5",
    strokeLinejoin: "round"
  }), /*#__PURE__*/React.createElement("path", {
    opacity: "0.25",
    d: "M268.14 13.4609H269.392V95.5379H268.14V13.4609Z",
    fill: isDarkTheme ? '#000000' : '#FFFFFF',
    fillOpacity: "0.5"
  })), /*#__PURE__*/React.createElement("div", {
    className: withThemeClasses(styles$a['coupon-interaction-content']),
    style: {
      top: vw(20),
      left: vw(16)
    }
  }, /*#__PURE__*/React.createElement("h5", {
    className: withThemeClasses(styles$a['coupon-interaction-subtitle']),
    style: {
      fontSize: vw(14),
      lineHeight: vw(19.6)
    }
  }, coupon_subtitle), /*#__PURE__*/React.createElement("h2", {
    className: withThemeClasses(styles$a['coupon-interaction-title']),
    style: {
      paddingTop: vw(8),
      fontSize: vw(36),
      lineHeight: vw(43.2)
    }
  }, coupon_title))), guide_texts && guide_texts.length > 0 && /*#__PURE__*/React.createElement("ul", {
    className: withThemeClasses(styles$a['coupon-guide-texts']),
    style: {
      paddingTop: vw(13),
      paddingLeft: vw(18)
    }
  }, guide_texts.map(function (guide) {
    return /*#__PURE__*/React.createElement("li", {
      key: guide,
      style: {
        fontSize: vw(11),
        lineHeight: vw(15.4),
        letterSpacing: vw(-0.1)
      }
    }, guide);
  })));
};

var QuickBenefitTypeBModule = function QuickBenefitTypeBModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "QuickBenefitTypeB"
  });
};

var QuickCarouselTypeAModule = function QuickCarouselTypeAModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "QuickCarouselTypeA"
  });
};

var QuickCarouselTypeBModule = function QuickCarouselTypeBModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "QuickCarouselTypeB"
  });
};

var styles$9 = {
  "quick-store-a": "QuickStoreTypeA-module__quick-store-a___3T3-y",
  "store-background": "QuickStoreTypeA-module__store-background___3PTyT",
  "store-contents": "QuickStoreTypeA-module__store-contents___2d4vO",
  "store-background-dim": "QuickStoreTypeA-module__store-background-dim___2t4UY",
  "store-overlay": "QuickStoreTypeA-module__store-overlay___23OcJ",
  "store-info": "QuickStoreTypeA-module__store-info___3osMP",
  "store-title": "QuickStoreTypeA-module__store-title___NvhCQ",
  "store-subtitle": "QuickStoreTypeA-module__store-subtitle___gf-0e",
  "action-button": "QuickStoreTypeA-module__action-button___1NvqJ"
};
function QuickStoreTypeAModule(_ref) {
  var title = _ref.title,
      _subtitle = _ref.subtitle,
      _ref$button_for = _ref.button_for,
      button_for = _ref$button_for === void 0 ? 'home' : _ref$button_for,
      action_home = _ref.action_home,
      button_text = _ref.button_text,
      action_favorite = _ref.action_favorite,
      background_image = _ref.background_image,
      store_action = _ref.store_action,
      overlay_opacity = _ref.overlay_opacity;

  var _useLayoutVariables = useLayoutVariables(),
      container_max_width = _useLayoutVariables.container_max_width;

  var vw = useViewSizeBaseOnDeviceWidth(container_max_width);

  var _useAction = useAction(action_home != null ? action_home : null),
      dispatchLinkHome = _useAction.dispatch;

  var _useAction2 = useAction(action_favorite != null ? action_favorite : null),
      dispatchAddFavorite = _useAction2.dispatch;

  var is_connected_store = Boolean(store_action);

  var _useMemo = useMemo(function () {
    var buttonMap = {
      button: {
        label: button_text != null ? button_text : '스토어 홈',
        onClick: function onClick() {
          if (action_home) {
            dispatchLinkHome();
          }
        }
      },
      subtitle: {
        label: _subtitle != null ? _subtitle : '즐겨찾기 추가',
        onClick: function onClick() {
          if (action_favorite) {
            dispatchAddFavorite();
          }
        }
      }
    };

    if (button_for === 'favorite') {
      var temp = buttonMap.subtitle;
      buttonMap.subtitle = buttonMap.button;
      buttonMap.button = temp;
      return buttonMap;
    }

    return buttonMap;
  }, [_subtitle, action_favorite, action_home, button_for, button_text, dispatchAddFavorite, dispatchLinkHome]),
      button = _useMemo.button,
      subtitle = _useMemo.subtitle;

  return /*#__PURE__*/React.createElement("div", {
    className: styles$9['quick-store-a'],
    style: {
      height: vw(192),
      padding: vw(16)
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$9['store-contents'],
    style: {
      borderRadius: vw(12)
    }
  }, /*#__PURE__*/React.createElement("img", {
    className: styles$9['store-background'],
    alt: "",
    src: background_image == null ? void 0 : background_image.src
  }), /*#__PURE__*/React.createElement("div", {
    className: styles$9['store-background-dim'],
    style: {
      opacity: overlay_opacity
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: styles$9['store-overlay'],
    style: {
      padding: vw(16)
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$9['store-info'],
    style: {
      left: vw(16),
      bottom: vw(24)
    },
    onClick: subtitle.onClick
  }, /*#__PURE__*/React.createElement("p", {
    className: styles$9['store-title'],
    style: {
      fontSize: vw(22),
      lineHeight: vw(30.8)
    }
  }, title), is_connected_store && /*#__PURE__*/React.createElement("button", {
    className: styles$9['store-subtitle'],
    style: {
      fontSize: vw(13),
      lineHeight: vw(16),
      gap: vw(3),
      marginTop: vw(8)
    }
  }, /*#__PURE__*/React.createElement("label", {
    style: {
      transform: "translateY(" + vw(1) + ")"
    }
  }, subtitle.label), /*#__PURE__*/React.createElement("div", {
    style: {
      width: vw(15),
      height: vw(15)
    }
  }, button_for === 'favorite' ? /*#__PURE__*/React.createElement(ArrowIcon, null) : /*#__PURE__*/React.createElement(PlusIcon, null)))), is_connected_store && /*#__PURE__*/React.createElement("button", {
    className: styles$9['action-button'],
    style: {
      right: vw(16),
      bottom: vw(16),
      fontSize: vw(13),
      lineHeight: vw(16),
      padding: vw(8) + " " + vw(14),
      borderRadius: vw(16)
    },
    onClick: button.onClick
  }, /*#__PURE__*/React.createElement("label", null, button.label), button_for === 'home' && /*#__PURE__*/React.createElement("div", {
    style: {
      width: vw(16),
      height: vw(16)
    }
  }, /*#__PURE__*/React.createElement(ArrowIcon, null))))));
}

function ArrowIcon() {
  return /*#__PURE__*/React.createElement("svg", {
    className: "store-home-icon",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    style: {
      width: '100%',
      height: '100%'
    }
  }, /*#__PURE__*/React.createElement("path", {
    d: "M6 3.5L10.5 8L6 12.5",
    stroke: "#FAFAFB"
  }));
}

function PlusIcon() {
  return /*#__PURE__*/React.createElement("svg", {
    width: "15",
    height: "14",
    viewBox: "0 0 15 14",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    style: {
      width: '100%',
      height: '100%'
    }
  }, /*#__PURE__*/React.createElement("path", {
    d: "M2.8125 7H12.1875",
    stroke: "#ffffff"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M7.5 2.625L7.5 11.375",
    stroke: "#ffffff"
  }));
}

var RelatedContentModule = function RelatedContentModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "RelatedContent"
  });
};

var ScrollableTabAreaModule = function ScrollableTabAreaModule(_ref) {
  var _tab_contents$0$uuid, _tab_contents$;

  var active_color = _ref.active_color,
      normal_color = _ref.normal_color,
      background_color = _ref.background_color,
      tab_contents = _ref.tab_contents,
      children = _ref.children;

  var _useState = useState((_tab_contents$0$uuid = (_tab_contents$ = tab_contents[0]) == null ? void 0 : _tab_contents$.uuid) != null ? _tab_contents$0$uuid : ''),
      active_tab_content_uuid = _useState[0],
      setActiveTabContentUuid = _useState[1];

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      backgroundColor: background_color
    }
  }, tab_contents.map(function (_ref2) {
    var title = _ref2.title,
        uuid = _ref2.uuid;
    return /*#__PURE__*/React.createElement("button", {
      key: uuid,
      style: {
        color: uuid === active_tab_content_uuid ? active_color : normal_color,
        backgroundColor: 'transparent'
      },
      onClick: function onClick() {
        return setActiveTabContentUuid(uuid);
      }
    }, title);
  })), React.Children.map(children, function (children, i) {
    return /*#__PURE__*/React.createElement("div", {
      key: i
    }, children);
  }));
};

var ShortFormVideoModule = function ShortFormVideoModule(props) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "ShortFormVideo"
  });
};

var SlotModule = function SlotModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "Slot"
  });
};

var MatchedStickerImageFinderContext = /*#__PURE__*/createContext(function () {
  return false;
});
MatchedStickerImageFinderContext.displayName = 'MatchedStickerImageFinderContext';

var StickerImageSwitch = function StickerImageSwitch(_ref) {
  var data_source = _ref.data_source;
  var findMatchedStickerImage = useContext(MatchedStickerImageFinderContext);

  var _useState = useState(data_source.conditional_images),
      conditional_images = _useState[0];

  var matched_image = conditional_images.find(findMatchedStickerImage);

  if (matched_image != null && matched_image.image) {
    return /*#__PURE__*/React.createElement(SingleImage, {
      data_source: matched_image.image
    });
  }

  if (data_source.fallback_image) {
    return /*#__PURE__*/React.createElement(SingleImage, {
      data_source: data_source.fallback_image
    });
  }

  return null;
};

var styles$8 = {
  "wrapper": "StickerGroup-module__wrapper___1jvCa",
  "stickerWrapper": "StickerGroup-module__stickerWrapper___2m34l"
};
var StickerPlacement = function StickerPlacement(_ref) {
  var data_source = _ref.data_source,
      scale = _ref.scale,
      children = _ref.children;
  var coordinates = data_source.coordinates;
  var offset = coordinates[0],
      size = coordinates[1];
  var left = offset[0],
      top = offset[1];
  var width = size[0],
      height = size[1];
  var scaled_top = top * scale;
  var scaled_left = left * scale;
  var scaled_width = width * scale;
  var scaled_height = height * scale;
  return /*#__PURE__*/React.createElement("div", {
    className: styles$8.stickerWrapper,
    style: {
      top: scaled_top,
      left: scaled_left,
      width: scaled_width,
      height: scaled_height
    }
  }, children);
};

var styles$7 = {
  "wrapper": "StickerGroup-module__wrapper___1jvCa",
  "stickerWrapper": "StickerGroup-module__stickerWrapper___2m34l"
};
var StickerGroupWithBackgroundImage = function StickerGroupWithBackgroundImage(_ref) {
  var background_image = _ref.background_image,
      stickers = _ref.stickers,
      enabled = _ref.enabled;
  var ref = useRef(null);

  var _useFlexibleSize = useFlexibleSize({
    ref: ref,
    base_width: 500
  }),
      scale = _useFlexibleSize.scale,
      is_parent_node_scale_inited = _useFlexibleSize.is_inited;

  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: styles$7.wrapper
  }, enabled ? /*#__PURE__*/React.createElement(BackgroundImage, {
    dataSource: background_image
  }) : /*#__PURE__*/React.createElement(Placeholder, {
    dataSource: background_image
  }), enabled && is_parent_node_scale_inited && stickers.map(function (sticker) {
    return /*#__PURE__*/React.createElement(StickerPlacement, {
      key: sticker.id,
      scale: scale,
      data_source: sticker
    }, /*#__PURE__*/React.createElement(StickerImageSwitch, {
      data_source: sticker
    }));
  }));
};

function BackgroundImage(_ref2) {
  var dataSource = _ref2.dataSource;
  return /*#__PURE__*/React.createElement(SingleImage, {
    data_source: dataSource
  });
}

function Placeholder(_ref3) {
  var dataSource = _ref3.dataSource;
  var width = dataSource.width,
      height = dataSource.height;
  var height_ratio = Math.floor(height / width * 1000) / 10;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      paddingBottom: height_ratio + '%'
    }
  });
}

var StickerGroupModule = function StickerGroupModule(_ref) {
  var background_image = _ref.background_image,
      stickers = _ref.stickers,
      _ref$enabled = _ref.enabled,
      enabled = _ref$enabled === void 0 ? true : _ref$enabled;

  if (!background_image) {
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  }

  return /*#__PURE__*/React.createElement(StickerGroupWithBackgroundImage, {
    background_image: background_image,
    stickers: stickers,
    enabled: enabled
  });
};

var StoreGridModule = function StoreGridModule(_) {
  return /*#__PURE__*/React.createElement(Skeleton, {
    name: "StoreGrid"
  });
};

var TabAreaModule = function TabAreaModule(_ref) {
  var _tab_contents$0$uuid, _tab_contents$;

  var active_color = _ref.active_color,
      normal_color = _ref.normal_color,
      background_color = _ref.background_color,
      tab_contents = _ref.tab_contents,
      children = _ref.children;

  /*
  MEMO: 모듈 노출 예약 때문에 UUID 기반으로 활성화 상태를 관리해야한다.
        그런데, 활성화된 UUID에 대한 children은 인덱스 기반으로밖에 못가져온다.
        (`ReactNode`, `ReactChildren`에서 props 등을 가져올 수 없음)
  */
  var _useState = useState((_tab_contents$0$uuid = (_tab_contents$ = tab_contents[0]) == null ? void 0 : _tab_contents$.uuid) != null ? _tab_contents$0$uuid : ''),
      active_tab_content_uuid = _useState[0],
      setActiveTabContentUuid = _useState[1];

  var active_tab_content_index = tab_contents.findIndex(function (_ref2) {
    var uuid = _ref2.uuid;
    return uuid === active_tab_content_uuid;
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      backgroundColor: background_color
    }
  }, tab_contents.map(function (_ref3) {
    var title = _ref3.title,
        uuid = _ref3.uuid;
    return /*#__PURE__*/React.createElement("button", {
      key: uuid,
      style: {
        color: uuid === active_tab_content_uuid ? active_color : normal_color,
        backgroundColor: 'transparent'
      },
      onClick: function onClick() {
        return setActiveTabContentUuid(uuid);
      }
    }, title);
  })), /*#__PURE__*/React.createElement("div", null, React.Children.toArray(children)[active_tab_content_index]));
};

var TabContentModule = function TabContentModule(props) {
  var tabIndex = useCurrentTabIndex();
  return /*#__PURE__*/React.createElement("div", {
    id: "panel-" + props.uuid,
    role: "tabpanel",
    tabIndex: tabIndex,
    "aria-labelledby": "tab-" + props.uuid
  }, props.children);
};

function useCurrentTabIndex() {
  var _useModuleHierarchy = useModuleHierarchy(),
      modules = _useModuleHierarchy.modules;

  try {
    var currentModule = modules[modules.length - 1];
    return currentModule.__index_at_parent;
  } catch (error) {
    return 0;
  }
}

var TextEditorModule = function TextEditorModule(_ref) {
  var html = _ref.html;
  return /*#__PURE__*/React.createElement("div", {
    dangerouslySetInnerHTML: {
      __html: html
    }
  });
};

var styles$6 = {
  "wrapper": "ToastMessage-module__wrapper___38Up4",
  "message": "ToastMessage-module__message___2h74l"
};
var ToastMessage = function ToastMessage(_ref) {
  var _ref$visible = _ref.visible,
      visible = _ref$visible === void 0 ? false : _ref$visible,
      _ref$transition_durat = _ref.transition_duration,
      transition_duration = _ref$transition_durat === void 0 ? 300 : _ref$transition_durat,
      toast_style = _ref.toast_style,
      overlay_style = _ref.overlay_style,
      children = _ref.children,
      onDestroy = _ref.onDestroy;
  useEffect(function () {
    var timeout = setTimeout(function () {
      if (!visible) {
        onDestroy == null ? void 0 : onDestroy();
      }
    }, transition_duration);
    return function () {
      return clearTimeout(timeout);
    };
  }, [onDestroy, transition_duration, visible]);
  return /*#__PURE__*/React.createElement("div", {
    className: styles$6.wrapper,
    style: overlay_style
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$6.message,
    style: _extends({
      opacity: visible ? 1 : 0,
      transition: "opacity " + transition_duration / 1000 + "s ease"
    }, toast_style)
  }, children));
};

var KEY = 'toast_message';
var toast_message_component = {
  ToastMessage: ToastMessage
};
function setToastMessageComponent(component) {
  toast_message_component.ToastMessage = component;
}
function toastMessage(_x, _x2, _x3) {
  return _toastMessage.apply(this, arguments);
}

function _toastMessage() {
  _toastMessage = _asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee(content, duration, _temp) {
    var _ref, _ref$transition_durat, transition_duration, toast_style, overlay_style, container, ToastMessage, props;

    return regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            if (duration === void 0) {
              duration = 3000;
            }

            _ref = _temp === void 0 ? {} : _temp, _ref$transition_durat = _ref.transition_duration, transition_duration = _ref$transition_durat === void 0 ? 300 : _ref$transition_durat, toast_style = _ref.toast_style, overlay_style = _ref.overlay_style;
            container = document.createElement('div');
            document.body.append(container);
            ToastMessage = toast_message_component.ToastMessage;
            props = {
              key: KEY,
              children: content,
              transition_duration: transition_duration,
              toast_style: toast_style,
              overlay_style: overlay_style
            };
            ReactDOM.render( /*#__PURE__*/React.createElement(ToastMessage, props), container);
            _context.next = 9;
            return halt(0);

          case 9:
            ReactDOM.render( /*#__PURE__*/React.createElement(ToastMessage, _extends({}, props, {
              visible: true
            })), container);
            _context.next = 12;
            return halt(transition_duration + duration);

          case 12:
            ReactDOM.render( /*#__PURE__*/React.createElement(ToastMessage, props), container);
            _context.next = 15;
            return halt(transition_duration);

          case 15:
            ReactDOM.unmountComponentAtNode(container);
            container.remove();

          case 17:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _toastMessage.apply(this, arguments);
}

function halt(duration) {
  return new Promise(function (res) {
    return setTimeout(res, duration);
  });
}

var styles$5 = {
  "header": "ToggleArea-module__header___UOiMp",
  "header-image": "ToggleArea-module__header-image___25bbi"
};
var ToggleAreaModule = function ToggleAreaModule(_ref) {
  var children = _ref.children,
      _ref$default_open = _ref.default_open,
      default_open = _ref$default_open === void 0 ? false : _ref$default_open,
      header = _ref.header;

  var _useState = useState(default_open),
      open = _useState[0],
      setOpen = _useState[1];

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: styles$5.header,
    onClick: function onClick() {
      return setOpen(function (open) {
        return !open;
      });
    }
  }, (header == null ? void 0 : header.type) === 'image' && /*#__PURE__*/React.createElement("img", {
    className: styles$5['header-image'],
    src: header.img_src,
    alt: open ? 'Click to collapse' : 'Click to expand'
  })), open && children);
};

function getLowQualityImage(image) {
  try {
    var src = image.src,
        width = image.width,
        height = image.height;
    var imgUrl = new URL(src);
    var searchParams = imgUrl.searchParams;
    searchParams.set('width', String(Math.floor(width / 20)));
    searchParams.set('height', String(Math.floor(height / 20)));
    searchParams.set('quality', '1');
    searchParams.set('format', 'webp');
    return _extends({}, image, {
      src: imgUrl.toString()
    });
  } catch (error) {
    return image;
  }
}

function getMiddleQualityImage(image) {
  try {
    var src = image.src,
        width = image.width,
        height = image.height;
    var imgUrl = new URL(src);
    var searchParams = imgUrl.searchParams;
    searchParams.set('width', String(Math.floor(width / 2)));
    searchParams.set('height', String(Math.floor(height / 2)));
    searchParams.set('quality', '50');
    searchParams.set('format', 'webp');
    return _extends({}, image, {
      src: imgUrl.toString()
    });
  } catch (error) {
    return image;
  }
}

function useProgressiveQualityImage(_ref) {
  var originalImage = _ref.originalImage,
      enabled = _ref.enabled;
  var originalImageLoadedRef = useRef(false);

  var _useState = useState(getLowQualityImage(originalImage)),
      displayingImage = _useState[0],
      setDisplayingImage = _useState[1];

  useEffect(function () {
    if (enabled) {
      try {
        preloadMiddleQualityImageIfOriginalImageNotLoaded();
        preloadOriginalImageAndMarkMiddleQualityImageAsNotNeeded();
      } catch (error) {
        accidentlyShowOriginalImage();
      }
    }

    function preloadOriginalImageAndMarkMiddleQualityImageAsNotNeeded() {
      var preloadOriginalImage = new Image();

      preloadOriginalImage.onload = function () {
        originalImageLoadedRef.current = true;
        setDisplayingImage(originalImage);
      };

      preloadOriginalImage.src = originalImage.src;
    }

    function preloadMiddleQualityImageIfOriginalImageNotLoaded() {
      var preloadMiddleQualityImage = new Image();
      var middleQualityImage = getMiddleQualityImage(originalImage);

      preloadMiddleQualityImage.onload = function () {
        if (!originalImageLoadedRef.current) {
          setDisplayingImage(middleQualityImage);
        }
      };

      preloadMiddleQualityImage.src = middleQualityImage.src;
    }

    function accidentlyShowOriginalImage() {
      setDisplayingImage(originalImage);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [enabled]);
  return displayingImage;
}

var useIntersectionObserver = function useIntersectionObserver(onIntersect, options) {
  var _useState = useState(null),
      target = _useState[0],
      setTarget = _useState[1];

  var onIntersect_ref = useRef(onIntersect);
  var options_ref = useRef(options);
  onIntersect_ref.current = onIntersect;
  options_ref.current = options;
  useEffect(function () {
    var observer;
    var options = options_ref.current;

    if (target) {
      var handleIntersect = function handleIntersect(entries, observer) {
        var onIntersect = onIntersect_ref.current;
        onIntersect(entries, observer);
      };

      observer = new IntersectionObserver(handleIntersect, options);
      observer.observe(target);
    } // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition


    return function () {
      return observer && observer.disconnect();
    };
  }, [target]);
  return setTarget;
};

var styles$4 = {
  "wrapper": "Video-module__wrapper___3TL2x",
  "clickToPlayUIWrapper": "Video-module__clickToPlayUIWrapper___3Wol3",
  "clickToPlayUIContent": "Video-module__clickToPlayUIContent___9Tf5m",
  "poster": "Video-module__poster___2aqDX",
  "soundControl": "Video-module__soundControl___8vZtj",
  "soundIcon": "Video-module__soundIcon___3vBid",
  "video": "Video-module__video___n-6mc"
};
var SoundOffIcon = function SoundOffIcon() {
  return /*#__PURE__*/React.createElement("svg", {
    className: styles$4.soundIcon,
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M15.75 10.4092L18.932 13.5912",
    stroke: "#242729",
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M18.9316 10.4092L15.7497 13.5912",
    stroke: "#242729",
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M8.22794 8.91176L12.75 6.5V17.5L8.22794 15.0882L8.0625 15H7.875H5.25V9H7.875H8.0625L8.22794 8.91176Z",
    stroke: "#242729",
    strokeWidth: "1.5"
  }));
};

var styles$3 = {
  "wrapper": "Video-module__wrapper___3TL2x",
  "clickToPlayUIWrapper": "Video-module__clickToPlayUIWrapper___3Wol3",
  "clickToPlayUIContent": "Video-module__clickToPlayUIContent___9Tf5m",
  "poster": "Video-module__poster___2aqDX",
  "soundControl": "Video-module__soundControl___8vZtj",
  "soundIcon": "Video-module__soundIcon___3vBid",
  "video": "Video-module__video___n-6mc"
};
var SoundOnIcon = function SoundOnIcon() {
  return /*#__PURE__*/React.createElement("svg", {
    className: styles$3.soundIcon,
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M8.22794 8.91176L12.75 6.5V17.5L8.22794 15.0882L8.0625 15H7.875H5.25V9H7.875H8.0625L8.22794 8.91176Z",
    stroke: "#242729",
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M18.75 7.5V16.5",
    stroke: "#242729",
    strokeWidth: "1.5"
  }), /*#__PURE__*/React.createElement("path", {
    d: "M15.75 9.75V14.25",
    stroke: "#242729",
    strokeWidth: "1.5"
  }));
};

var I18NextInjectionContext = /*#__PURE__*/createContext({
  t: function t(i) {
    return i;
  },
  Trans: function Trans(_ref) {
    var children = _ref.children,
        i18nKey = _ref.i18nKey;
    return i18nKey || children;
  }
});
I18NextInjectionContext.displayName = 'I18NextInjectionContext';
function useInjectedI18Next() {
  return useContext(I18NextInjectionContext);
}

var styles$2 = {
  "wrapper": "Video-module__wrapper___3TL2x",
  "clickToPlayUIWrapper": "Video-module__clickToPlayUIWrapper___3Wol3",
  "clickToPlayUIContent": "Video-module__clickToPlayUIContent___9Tf5m",
  "poster": "Video-module__poster___2aqDX",
  "soundControl": "Video-module__soundControl___8vZtj",
  "soundIcon": "Video-module__soundIcon___3vBid",
  "video": "Video-module__video___n-6mc"
};
var SuspendedPlayButton = function SuspendedPlayButton(_ref) {
  var onClick = _ref.onClick;

  var _useInjectedI18Next = useInjectedI18Next(),
      t = _useInjectedI18Next.t;

  return /*#__PURE__*/React.createElement("div", {
    className: styles$2.clickToPlayUIWrapper,
    onClick: onClick
  }, /*#__PURE__*/React.createElement("div", {
    className: styles$2.clickToPlayUIContent
  }, t('저전력 모드로 영상이 자동 재생되지 않습니다'), /*#__PURE__*/React.createElement("br", null), t('재생 버튼을 눌러주세요')));
};

function fadeInVolume(_ref) {
  var videoElement = _ref.videoElement,
      callback = _ref.callback;
  fadeVolume({
    videoElement: videoElement,
    type: 'in',
    callback: callback
  });
}
function fadeOutVolume(_ref2) {
  var videoElement = _ref2.videoElement,
      callback = _ref2.callback;
  fadeVolume({
    videoElement: videoElement,
    type: 'out',
    callback: callback
  });
}

function fadeVolume(_ref3) {
  var videoElement = _ref3.videoElement,
      type = _ref3.type,
      _callback = _ref3.callback;

  try {
    if (!videoElement) {
      return;
    }

    var startValue = type === 'in' ? 0 : videoElement.volume;
    var targetValue = type === 'in' ? 1 : 0;
    var durationInMillis = type === 'in' ? 1000 : 300;
    animateVolume({
      startValue: startValue,
      targetValue: targetValue,
      durationInMillis: durationInMillis,
      callback: function callback(value) {
        videoElement.volume = value;

        if (value === targetValue) {
          _callback == null ? void 0 : _callback();
        }
      }
    });
  } catch (error) {
    console.log(error);
  }
}

function animateVolume(_ref4) {
  var startValue = _ref4.startValue,
      targetValue = _ref4.targetValue,
      durationInMillis = _ref4.durationInMillis,
      callback = _ref4.callback;
  var startTime = getNow();
  animate();

  function animate() {
    try {
      var currentTime = getNow();
      var elapsedTime = currentTime - startTime;
      var progress = elapsedTime / durationInMillis;

      if (progress >= 1) {
        callback(targetValue);
        return;
      }

      var easedProgressValue = easeInCircle({
        currentStep: progress,
        startValue: startValue,
        targetValue: targetValue,
        totalSteps: 1
      });
      callback(easedProgressValue);
      requestAnimationFrame(animate);
    } catch (error) {
      callback(targetValue);
    }
  }
}

function easeInCircle(_ref5) {
  var currentStep = _ref5.currentStep,
      startValue = _ref5.startValue,
      targetValue = _ref5.targetValue,
      totalSteps = _ref5.totalSteps;

  try {
    var delta = targetValue - startValue;
    var result = startValue + delta * (1 - Math.sqrt(1 - Math.pow(currentStep / totalSteps, 2)));

    if (isNaN(result)) {
      throw new Error('result is NaN');
    }

    return result;
  } catch (error) {
    if (currentStep === 0) {
      return startValue;
    }

    return targetValue;
  }
}

function getNow() {
  try {
    return performance.now();
  } catch (error) {
    return Date.now();
  }
}

function usePosterImageAutoRemoveWhenVideoCanPlay(_ref) {
  var poster_image_ref = _ref.poster_image_ref,
      video_ref = _ref.video_ref,
      enabled = _ref.enabled;
  useEffect(function () {
    if (!enabled || !poster_image_ref.current || !video_ref.current) {
      return;
    }

    var video = video_ref.current;
    var poster_image = poster_image_ref.current;

    var handleCanPlay = function handleCanPlay() {
      try {
        // NOTE: atf 측정시 IntersectionObserver에 잡히도록 display: none; 대신 visibiliy: hidden; 사용
        poster_image.style.visibility = 'hidden';
      } catch (err) {
        console.log(err);
      }
    };

    video.addEventListener('canplay', handleCanPlay);
    return function () {
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [enabled, poster_image_ref, video_ref]);
}

var styles$1 = {
  "wrapper": "Video-module__wrapper___3TL2x",
  "clickToPlayUIWrapper": "Video-module__clickToPlayUIWrapper___3Wol3",
  "clickToPlayUIContent": "Video-module__clickToPlayUIContent___9Tf5m",
  "poster": "Video-module__poster___2aqDX",
  "soundControl": "Video-module__soundControl___8vZtj",
  "soundIcon": "Video-module__soundIcon___3vBid",
  "video": "Video-module__video___n-6mc"
};
var VideoModule = function VideoModule(_ref) {
  var video_url = _ref.video_url,
      poster_image = _ref.poster_image,
      poster_image_library = _ref.poster_image_library,
      content_muted = _ref.muted,
      width = _ref.width,
      height = _ref.height,
      wrapper_style = _ref.wrapper_style,
      video_style = _ref.video_style,
      enabled = _ref.enabled;

  /** 사용처에서 `enabled`를 지정한 경우 인터섹션 상태를 제어 컴포넌트 방식으로 구현 */
  var isIntersectionControlled = typeof enabled === 'boolean';
  /** 제어/비제어 구분 없이 최초 노출 되었는지 여부 */

  var _useState = useState(false),
      intersectionFirstEntered = _useState[0],
      setIntersectionFirstEntered = _useState[1];

  var heightRatio = Math.floor(height / width * 1000) / 10;
  var withAspectRatio = !isNaN(heightRatio);

  var _useState2 = useState(false),
      suspended = _useState2[0],
      setSuspended = _useState2[1];

  var posterRef = useRef(null);
  var videoRef = useRef(null);

  var playWithSuspendedStateHandling = function playWithSuspendedStateHandling() {
    if (!videoRef.current) {
      return;
    }

    videoRef.current.play().then(function () {
      return setSuspended(false);
    })["catch"](function (error) {
      if (error instanceof Error && error.name === 'NotAllowedError') {
        setSuspended(true);
      }
    });
  };

  var hasSound = !content_muted;

  var _useState3 = useState(true),
      muted = _useState3[0],
      setMuted = _useState3[1];

  var onFadeOutRef = useRef(function () {});

  var muteVideo = function muteVideo() {
    setMuted(true);

    onFadeOutRef.current = function () {
      if (!videoRef.current) {
        return;
      }

      videoRef.current.muted = true;
    };

    fadeOutVolume({
      videoElement: videoRef.current,
      callback: function callback() {
        try {
          onFadeOutRef.current();
        } catch (error) {
          console.log(error);
        }
      }
    });
  };

  var unmuteVideo = function unmuteVideo() {
    if (!videoRef.current) {
      return;
    }

    onFadeOutRef.current = function () {};

    videoRef.current.muted = false;
    setMuted(false);
    fadeInVolume({
      videoElement: videoRef.current
    });
  };

  var toggleMuteVideo = function toggleMuteVideo() {
    if (muted) {
      unmuteVideo();
    } else {
      muteVideo();
    }
  };

  var onAutoPauseRef = useRef(function () {});

  var playVideoWithVolumeFadeIn = function playVideoWithVolumeFadeIn() {
    try {
      if (!videoRef.current) {
        return;
      }

      onAutoPauseRef.current = function () {};

      videoRef.current.volume = 0;
      playWithSuspendedStateHandling();
      fadeInVolume({
        videoElement: videoRef.current
      });
    } catch (error) {
      console.log(error);
    }
  };

  var volumeFadeOutAndPauseVideoWithResetCurrentTime = function volumeFadeOutAndPauseVideoWithResetCurrentTime() {
    try {
      if (suspended) {
        var preventPauseSoPosterImageNotRemoved = undefined;
        return preventPauseSoPosterImageNotRemoved;
      }

      if (!videoRef.current) {
        return;
      }

      onAutoPauseRef.current = function () {
        if (!videoRef.current) {
          return;
        }

        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      };

      fadeOutVolume({
        videoElement: videoRef.current,
        callback: function callback() {
          try {
            onAutoPauseRef.current();
          } catch (error) {
            console.log(error);
          }
        }
      });
    } catch (error) {
      console.log(error);
    }
  };

  var handleIntersectionEnter = useRef(function () {
    setIntersectionFirstEntered(true);
    setTimeout(playVideoWithVolumeFadeIn, 0);
  });
  var handleIntersectionExit = useRef(function () {
    volumeFadeOutAndPauseVideoWithResetCurrentTime();
  });
  var observableWrapperRef = useIntersectionObserver(function (_ref2) {
    var entry = _ref2[0];

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!entry || isIntersectionControlled) {
      return;
    }

    if (entry.isIntersecting) {
      handleIntersectionEnter.current();
    } else {
      handleIntersectionExit.current();
    }
  });
  useEffect(function () {
    if (!isIntersectionControlled) {
      return;
    }

    if (enabled) {
      handleIntersectionEnter.current();
    } else {
      handleIntersectionExit.current();
    }
  }, [enabled, isIntersectionControlled]);
  usePosterImageAutoRemoveWhenVideoCanPlay({
    poster_image_ref: posterRef,
    video_ref: videoRef,
    enabled: intersectionFirstEntered
  });
  var posterImage = useProgressiveQualityImage({
    enabled: intersectionFirstEntered,
    originalImage: poster_image_library != null ? poster_image_library : {
      action_map: {},
      area_map: {},
      width: width,
      height: height,
      id: 'video_poster_image',
      src: poster_image != null ? poster_image : ''
    }
  });
  return /*#__PURE__*/React.createElement("div", {
    ref: observableWrapperRef,
    className: styles$1.wrapper,
    style: _extends({}, withAspectRatio && {
      paddingBottom: heightRatio + '%'
    }, wrapper_style)
  }, suspended && /*#__PURE__*/React.createElement(SuspendedPlayButton, {
    onClick: playWithSuspendedStateHandling
  }), intersectionFirstEntered && /*#__PURE__*/React.createElement("img", {
    ref: posterRef,
    className: styles$1.poster,
    src: posterImage.src,
    alt: ""
  }), intersectionFirstEntered && /*#__PURE__*/React.createElement("video", {
    ref: videoRef,
    className: styles$1.video,
    playsInline: true,
    loop: true,
    muted: true,
    preload: "metadata",
    style: _extends({}, withAspectRatio && {
      position: 'absolute',
      top: -0.4,
      left: -0.4
    }, video_style)
  }, /*#__PURE__*/React.createElement("source", {
    src: video_url,
    type: "video/mp4"
  })), hasSound && /*#__PURE__*/React.createElement("div", {
    className: styles$1.soundControl,
    onClick: toggleMuteVideo
  }, muted ? /*#__PURE__*/React.createElement(SoundOffIcon, null) : /*#__PURE__*/React.createElement(SoundOnIcon, null)));
};

var YouTubeModule = function YouTubeModule(_ref) {
  var link = _ref.link,
      _ref$width_ratio = _ref.width_ratio,
      width_ratio = _ref$width_ratio === void 0 ? 1 : _ref$width_ratio,
      _ref$height_ratio = _ref.height_ratio,
      height_ratio = _ref$height_ratio === void 0 ? 1 : _ref$height_ratio,
      _ref$active = _ref.active,
      active = _ref$active === void 0 ? true : _ref$active;
  return /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'relative',
      paddingBottom: height_ratio / width_ratio * 100 + "%",
      overflow: 'hidden',
      backgroundColor: '#000'
    }
  }, active && /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      width: 'calc(100% + 0.8px)',
      height: 'calc(100% + 0.8px)',
      top: -0.4,
      left: -0.4
    }
  }, link && /*#__PURE__*/React.createElement("iframe", {
    width: "100%",
    height: "100%",
    src: link,
    title: "YouTube video player",
    frameBorder: "0",
    allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
    allowFullScreen: true
  })));
};

var _excluded$3 = ["children"];
var INITIAL_MODULE_MAP = {
  Animation: AnimationModule,
  AnimationScene: AnimationSceneModule,
  BasicTimer: BasicTimerModule,
  BlackWeekBest: BlackWeekBestModule,
  BlackWeekSpecialPrice: BlackWeekSpecialPriceModule,
  BlackWeekSpecialPriceProductList: BlackWeekSpecialPriceProductListModule,
  BlackWeekSpecialPriceSubscribeButton: BlackWeekSpecialPriceSubscribeButtonModule,
  BoxItem: BoxItemModule,
  ContentReply: ContentReplyModule,
  EventLayout: EventLayout,
  FloatingButton: FloatingButtonModule,
  FCFSCoupon: FCFSCouponModule,
  ImageCarousel: ImageCarouselModule,
  ImageSlide: ImageSlideModule,
  ImageRail: ImageRailModule,
  ImageMap: ImageMapModule,
  ProductList: ProductListModule,
  ProductTab: ProductTabModule,
  ProductFilter: ProductFilterModule,
  ProductRail: ProductRailModule,
  RelatedContent: RelatedContentModule,
  ScrollableTabArea: ScrollableTabAreaModule,
  Slot: SlotModule,
  StoreGrid: StoreGridModule,
  TabArea: TabAreaModule,
  TabContent: TabContentModule,
  TextEditor: TextEditorModule,
  ToggleArea: ToggleAreaModule,
  Video: VideoModule,
  YouTube: YouTubeModule,
  Dialog: Dialog,
  ToastMessage: ToastMessage,
  StickerGroup: StickerGroupModule,
  ShortFormVideo: ShortFormVideoModule,
  QuickArtworkTypeA: QuickArtworkTypeAModule,
  QuickArtworkTypeB: QuickArtworkTypeBModule,
  QuickBannerTypeA: QuickBannerTypeAModule,
  QuickBannerTypeB: QuickBannerTypeBModule,
  QuickBenefitTypeA: QuickBenefitTypeAModule,
  QuickBenefitTypeB: QuickBenefitTypeBModule,
  QuickCarouselTypeA: QuickCarouselTypeAModule,
  QuickCarouselTypeB: QuickCarouselTypeBModule,
  QuickStoreTypeA: QuickStoreTypeAModule,
  ProductCarousel: ProductCarouselModule,
  DescriptiveTimer: DescriptiveTimerModule
};
var ModuleMapContext = /*#__PURE__*/React.createContext(INITIAL_MODULE_MAP);
ModuleMapContext.displayName = 'ModuleMapContext';
function getMergedModules(modules) {
  return _extends({}, INITIAL_MODULE_MAP, modules);
}
function ModuleMapProvider(_ref) {
  var children = _ref.children,
      modules = _objectWithoutPropertiesLoose(_ref, _excluded$3);

  var _React$useState = React.useState(function () {
    return getMergedModules(modules);
  }),
      value = _React$useState[0];

  React.useEffect(function () {
    setDialogComponent(value.Dialog);
    setToastMessageComponent(value.ToastMessage);
    return function () {
      setDialogComponent(Dialog);
      setToastMessageComponent(ToastMessage);
    };
  }, [value.Dialog, value.ToastMessage]);
  return /*#__PURE__*/React.createElement(ModuleMapContext.Provider, {
    value: value
  }, children);
}

function getPartialTabContents(props, _ref) {
  var component_map = _ref.component_map,
      component_relations = _ref.component_relations;

  if (!isTabModule(props)) {
    return undefined;
  }

  return component_relations.map(function (_ref2) {
    var uuid = _ref2.uuid;

    try {
      var child_module = component_map[uuid];

      if (child_module.__type === 'TabContent') {
        return child_module;
      }

      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }).filter(Boolean);
}
function getPartialTabButtons(props, _ref3) {
  var component_map = _ref3.component_map,
      component_relations = _ref3.component_relations;

  if (!isTabModule(props)) {
    return undefined;
  }

  return component_relations.map(function (_ref4) {
    var uuid = _ref4.uuid;

    try {
      var child_module = component_map[uuid];

      if (child_module.__type === 'TabContent') {
        return child_module.title;
      }

      return '';
    } catch (error) {
      console.error(error);
      return '';
    }
  }).filter(Boolean);
}
function getTabContentTree(_ref5) {
  var component_map = _ref5.component_map,
      component_relations = _ref5.component_relations,
      TabContent = _ref5.TabContent,
      BoxItem = _ref5.BoxItem,
      modules = _ref5.modules;
  return component_relations.map(function (_ref6) {
    var uuid = _ref6.uuid,
        _ref6$children = _ref6.children,
        children = _ref6$children === void 0 ? [] : _ref6$children;

    try {
      var props = component_map[uuid];

      if (props.__type === 'TabContent') {
        return /*#__PURE__*/React.createElement(ModuleHierarchyContext.Provider, {
          value: {
            modules: modules.concat(props)
          }
        }, /*#__PURE__*/React.createElement(BoxItem, {
          uuid: props.uuid,
          module_background_color: props.module_background_color
        }, /*#__PURE__*/React.createElement(TabContent, _extends({
          key: uuid
        }, props), /*#__PURE__*/React.createElement(ModuleTree, {
          component_map: component_map,
          component_relations: children
        }))));
      }

      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }).filter(Boolean);
}

var _excluded$2 = ["child_modules"];
var ModuleMapper = function ModuleMapper(_ref) {
  var child_modules = _ref.child_modules,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$2);

  var injected_modules = useContext(ModuleMapContext);
  var BoxItem = injected_modules.BoxItem;
  var Component = injected_modules[props.__type];
  var component_map = useComponentMap();

  var _useModuleHierarchy = useModuleHierarchy(),
      inherit_modules = _useModuleHierarchy.modules;

  if (Component) {
    /**
     * @deprecated
     */
    var buttons = getPartialTabButtons(props, {
      component_map: component_map,
      component_relations: child_modules != null ? child_modules : []
    });
    var tab_contents = getPartialTabContents(props, {
      component_map: component_map,
      component_relations: child_modules != null ? child_modules : []
    });
    var modules = inherit_modules.concat(props);
    return /*#__PURE__*/React.createElement(ModuleHierarchyContext.Provider, {
      value: {
        modules: modules
      }
    }, /*#__PURE__*/React.createElement(BoxItem, {
      uuid: props.uuid,
      module_background_color: props.module_background_color
    }, /*#__PURE__*/React.createElement(Component, _extends({}, props, buttons && {
      buttons: buttons
    }, tab_contents && {
      tab_contents: tab_contents
    }), child_modules && (isTabModule(props) ? getTabContentTree({
      component_map: component_map,
      component_relations: child_modules,
      TabContent: injected_modules.TabContent,
      BoxItem: injected_modules.BoxItem,
      modules: modules
    }) : /*#__PURE__*/React.createElement(ModuleTree, {
      component_map: component_map,
      component_relations: child_modules
    })))));
  }

  return null;
};

function getUnifiedItemMap(component_map) {
  var allModules = Object.values(component_map);
  var slotModules = allModules.filter(isSlotModule);
  var unifiedActionMap = {};
  var unifiedImageMap = {};
  slotModules.forEach(function (_ref) {
    var content_action_map = _ref.content_action_map,
        image_map = _ref.image_map;
    unifiedActionMap = _extends({}, unifiedActionMap, content_action_map);
    unifiedImageMap = _extends({}, unifiedImageMap, image_map);
  });
  return {
    unifiedActionMap: unifiedActionMap,
    unifiedImageMap: unifiedImageMap
  };
}

var UnifiedSlotActionMapContext = /*#__PURE__*/createContext({});
var UnifiedSlotImageMapContext = /*#__PURE__*/createContext({});
function UnifiedSlotItemMapProvider(_ref2) {
  var component_map = _ref2.component_map,
      children = _ref2.children;

  var _getUnifiedItemMap = getUnifiedItemMap(component_map),
      unifiedActionMap = _getUnifiedItemMap.unifiedActionMap,
      unifiedImageMap = _getUnifiedItemMap.unifiedImageMap;

  return /*#__PURE__*/React.createElement(UnifiedSlotActionMapContext.Provider, {
    value: unifiedActionMap
  }, /*#__PURE__*/React.createElement(UnifiedSlotImageMapContext.Provider, {
    value: unifiedImageMap
  }, children));
}
function useSlotAction(action_id) {
  var unifiedActionMap = useContext(UnifiedSlotActionMapContext);
  var action = useAction(unifiedActionMap[action_id]);
  return action;
}
function useSlotImage(image_id) {
  var unifiedImageMap = useContext(UnifiedSlotImageMapContext);
  var image = unifiedImageMap[image_id];
  return image;
}

var _excluded$1 = ["next_head"];

/**
 * 도큐먼트 헤드에 타이틀, 디스크립션, OG태그 정보를 추가해준다.
 *
 * `<ModuleRoot />`를 사용하는 경우 내부에서 마운트되기 때문에 별도로 렌더링해주지 않아도 된다.
 */
var OpenGraphHead = function OpenGraphHead(_ref) {
  var Head = _ref.next_head,
      props = _objectWithoutPropertiesLoose(_ref, _excluded$1);

  if (!Head) {
    return null;
  }

  return /*#__PURE__*/React.createElement(Head, null, /*#__PURE__*/React.createElement("title", null, props.page_title), /*#__PURE__*/React.createElement("meta", {
    property: "og:title",
    content: props.title || props.page_title
  }), /*#__PURE__*/React.createElement("meta", {
    property: "og:type",
    content: "article"
  }), /*#__PURE__*/React.createElement("meta", {
    property: "og:url",
    content: props.page_url
  }), props.description && /*#__PURE__*/React.createElement("meta", {
    property: "description",
    content: props.description
  }), props.description && /*#__PURE__*/React.createElement("meta", {
    property: "og:description",
    content: props.description
  }), props.image_src && /*#__PURE__*/React.createElement("meta", {
    property: "og:image",
    content: props.image_src
  }));
};

var _excluded = ["next_head", "share_info", "component_map", "component_relations", "page_background_color", "t", "Trans", "action_map", "module_map", "custom_data", "layout_variables", "findMatchedStickerImage"];

/**
 * 콘텐츠 에디터 정보로부터 모듈들을 그려나가는 루트 컴포넌트
 *
 * 각 사이트(지그재그, 포스티, 패바카 등)의 이벤트 페이지 라우트에서 렌더링하여 사용한다.
 */
function ModuleRoot(_ref) {
  var next_head = _ref.next_head,
      share_info = _ref.share_info,
      component_map = _ref.component_map,
      component_relations = _ref.component_relations,
      page_background_color = _ref.page_background_color,
      _ref$t = _ref.t,
      t = _ref$t === void 0 ? function (i18nKey) {
    return i18nKey;
  } : _ref$t,
      _ref$Trans = _ref.Trans,
      Trans = _ref$Trans === void 0 ? function (_ref2) {
    var i18nKey = _ref2.i18nKey,
        children = _ref2.children;
    return i18nKey || children;
  } : _ref$Trans,
      _ref$action_map = _ref.action_map,
      action_map = _ref$action_map === void 0 ? {} : _ref$action_map,
      _ref$module_map = _ref.module_map,
      module_map = _ref$module_map === void 0 ? {} : _ref$module_map,
      _ref$custom_data = _ref.custom_data,
      custom_data = _ref$custom_data === void 0 ? {} : _ref$custom_data,
      _ref$layout_variables = _ref.layout_variables,
      layout_variables = _ref$layout_variables === void 0 ? {} : _ref$layout_variables,
      _ref$findMatchedStick = _ref.findMatchedStickerImage,
      findMatchedStickerImage = _ref$findMatchedStick === void 0 ? function () {
    return false;
  } : _ref$findMatchedStick,
      metadata = _objectWithoutPropertiesLoose(_ref, _excluded);

  var _useState = useState(function () {
    return Date.now();
  }),
      current_time = _useState[0];

  var _useState2 = useState(function () {
    return getMergedModules(module_map);
  }),
      EventLayout = _useState2[0].EventLayout;

  useInitializeClient();
  return /*#__PURE__*/React.createElement(UnifiedSlotItemMapProvider, {
    component_map: component_map
  }, /*#__PURE__*/React.createElement(ResizeEventListenerContextProvider, null, /*#__PURE__*/React.createElement(MatchedStickerImageFinderContext.Provider, {
    value: findMatchedStickerImage
  }, /*#__PURE__*/React.createElement(LayoutVariablesProvider, {
    value: layout_variables
  }, /*#__PURE__*/React.createElement(ContainerWidthProvider, null, /*#__PURE__*/React.createElement(I18NextInjectionContext.Provider, {
    value: {
      t: t,
      Trans: Trans
    }
  }, /*#__PURE__*/React.createElement(ModuleMapProvider, module_map, /*#__PURE__*/React.createElement(ContentMetadataContext.Provider, {
    value: _extends({
      current_time: current_time,
      custom_data: custom_data
    }, metadata, share_info)
  }, /*#__PURE__*/React.createElement(UserInputContext.Provider, {
    value: useUserInputState()
  }, /*#__PURE__*/React.createElement(ActionProvider, {
    action_map: mergeActionMapOptions(action_map)
  }, /*#__PURE__*/React.createElement(OpenGraphHead, {
    next_head: next_head,
    page_title: metadata.page_title,
    page_url: metadata.page_url,
    title: share_info.title,
    description: share_info.description,
    image_src: share_info.image_src
  }), /*#__PURE__*/React.createElement(ComponentMapContext.Provider, {
    value: component_map
  }, /*#__PURE__*/React.createElement(EventLayout, {
    page_background_color: page_background_color
  }, /*#__PURE__*/React.createElement(ModuleTree, {
    component_map: component_map,
    component_relations: component_relations
  })))))))))))));
}

function useInitializeClient() {
  useEffect(function () {
    initializeClient();
  }, []);
}

function initializeClient() {
  if (typeof document !== 'undefined') {
    document.addEventListener('contextmenu', preventDisallowedMouseEvent);
  }
}

var ALLOWED_NODE_NAME_LIST = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'blockquote', 'q', 'article'];

function preventDisallowedMouseEvent(e) {
  var target = e.target;

  if (target instanceof HTMLElement && ALLOWED_NODE_NAME_LIST.includes(target.nodeName)) {
    return;
  }

  e.preventDefault();
}

var ActionReadyState;

(function (ActionReadyState) {
  ActionReadyState["IDLE"] = "IDLE";
  ActionReadyState["LOADING"] = "LOADING";
  ActionReadyState["COMPLETE"] = "COMPLETE";
})(ActionReadyState || (ActionReadyState = {}));

var ActionMapContext = /*#__PURE__*/createContext({});
ActionMapContext.displayName = 'ActionMapContext';
var ActionProvider = function ActionProvider(_ref) {
  var children = _ref.children,
      action_map = _ref.action_map;
  return /*#__PURE__*/React.createElement(ActionMapContext.Provider, {
    value: action_map != null ? action_map : {}
  }, children);
};
function useAction(action) {
  var action_map = useContext(ActionMapContext);
  var i18next = useInjectedI18Next();
  var metadata = useContentMetadata();
  var module_hierarchy = useModuleHierarchy();
  var current_action = action ? action_map[action.type] : {};
  var action_ref = useRef(action);
  var initializer_ref = useRef(current_action == null ? void 0 : current_action.initializer);
  var action_context_ref = useRef({
    i18next: i18next,
    metadata: metadata,
    module_hierarchy: module_hierarchy
  });

  var _useState = useState({
    ready_state: ActionReadyState.IDLE,
    disabled: false,
    dispatching: false,
    payload: undefined
  }),
      action_state = _useState[0],
      setActionState = _useState[1];

  useEffect(function () {
    initializer_ref.current == null ? void 0 : initializer_ref.current(action_ref.current, action_context_ref.current).then(setActionState);
  }, []);
  revalidateLatestRefValuesDependModuleRootProps(action_context_ref, i18next, metadata);
  return {
    state: action_state,
    dispatch: function dispatch() {
      return new Promise(function (resolve) {
        if (action_state.ready_state === ActionReadyState.COMPLETE && !action_state.disabled && !action_state.dispatching) {
          setActionState(function (action_state) {
            return _extends({}, action_state, {
              dispatching: true
            });
          });
          var handler_promise = current_action == null ? void 0 : current_action.handler == null ? void 0 : current_action.handler(action, action_context_ref.current, action_state).then(function (next_action_state) {
            return setActionState(next_action_state ? next_action_state : function (action_state) {
              return _extends({}, action_state, {
                dispatching: false
              });
            });
          })["catch"](function () {
            return setActionState(function (action_state) {
              return _extends({}, action_state, {
                dispatching: false,
                disabled: true
              });
            });
          });
          return resolve(handler_promise);
        }
      });
    }
  };
}

function revalidateLatestRefValuesDependModuleRootProps(action_context_ref, i18next, metadata) {
  action_context_ref.current.i18next = i18next;
  action_context_ref.current.metadata = metadata;
}

function defaultHandler() {
  return new Promise(function (res) {
    return res;
  });
}

function getInitializedState() {
  return new Promise(function (res) {
    return res({
      ready_state: ActionReadyState.COMPLETE,
      disabled: false,
      dispatching: false,
      payload: undefined
    });
  });
}

function mergeActionMapOptions(action_map) {
  return {
    MANUAL_DEEP_LINK: _extends({
      handler: defaultHandler,
      initializer: getInitializedState
    }, action_map.MANUAL_DEEP_LINK),
    PDP_DEEP_LINK: _extends({
      handler: defaultHandler,
      initializer: getInitializedState
    }, action_map.PDP_DEEP_LINK),
    STORE_HOME_DEEP_LINK: _extends({
      handler: defaultHandler,
      initializer: getInitializedState
    }, action_map.STORE_HOME_DEEP_LINK),
    EVENT_METADATA: _extends({
      handler: defaultHandler,
      initializer: getInitializedState
    }, action_map.EVENT_METADATA),
    MARKETING_CONSENT: _extends({
      handler: defaultHandler,
      initializer: getInitializedState
    }, action_map.MARKETING_CONSENT),
    KAKAO_SHARE: _extends({
      handler: defaultHandler,
      initializer: getInitializedState
    }, action_map.KAKAO_SHARE),
    FACEBOOK_SHARE: _extends({
      handler: defaultHandler,
      initializer: getInitializedState
    }, action_map.FACEBOOK_SHARE),
    URL_SHARE: _extends({
      handler: defaultHandler,
      initializer: getInitializedState
    }, action_map.URL_SHARE),
    ADD_FAVORITE_STORE: _extends({
      handler: defaultHandler,
      initializer: getInitializedState
    }, action_map.ADD_FAVORITE_STORE)
  };
}

var styles = {
  "area": "ImageArea-module__area___3rDvR"
};
var ImageArea = function ImageArea(_ref) {
  var area = _ref.area,
      action = _ref.action,
      image_size_correction_ratio = _ref.image_size_correction_ratio;

  var _useAction = useAction(action),
      dispatch = _useAction.dispatch;

  if (area.type === AreaShapeType.RECT) {
    var _area$coordinates = area.coordinates,
        _area$coordinates$ = _area$coordinates[0],
        x = _area$coordinates$[0],
        y = _area$coordinates$[1],
        _area$coordinates$2 = _area$coordinates[1],
        width = _area$coordinates$2[0],
        height = _area$coordinates$2[1];
    var x1 = Math.round(x * image_size_correction_ratio);
    var y1 = Math.round(y * image_size_correction_ratio);
    var x2 = Math.round((x + width) * image_size_correction_ratio);
    var y2 = Math.round((y + height) * image_size_correction_ratio);
    return /*#__PURE__*/React.createElement("div", {
      className: styles.area,
      style: {
        left: x1,
        top: y1,
        width: x2 - x1,
        height: y2 - y1
      },
      onClick: dispatch
    });
  }

  return null;
};

var ConditionalStatus;

(function (ConditionalStatus) {
  ConditionalStatus["USER_LOGGED_IN"] = "USER_LOGGED_IN";
  ConditionalStatus["USER_NOT_LOGGED_IN"] = "USER_NOT_LOGGED_IN";
  ConditionalStatus["EVENT_METADATA_PARTICIPATED"] = "EVENT_METADATA_PARTICIPATED";
  ConditionalStatus["EVENT_METADATA_NOT_PARTICIPATED"] = "EVENT_METADATA_NOT_PARTICIPATED";
  ConditionalStatus["EVENT_METADATA_IN_PROGRESS"] = "EVENT_METADATA_IN_PROGRESS";
  ConditionalStatus["EVENT_METADATA_NOT_IN_PROGRESS"] = "EVENT_METADATA_NOT_IN_PROGRESS";
})(ConditionalStatus || (ConditionalStatus = {}));

export { ActionMapContext, ActionProvider, ActionReadyState, ActionType, AnimationModule, AnimationSceneModule, AreaShapeType, BasicTimerModule, BlackWeekBestModule, BlackWeekSpecialPriceModule, BlackWeekSpecialPriceProductListModule, BlackWeekSpecialPriceSubscribeButtonModule, BoxItemModule, ClipType, ComponentMapContext, ConditionalStatus, ContainerWidthContext, ContainerWidthProvider, ContentCountry, ContentCurrency, ContentEditorAgent, ContentEditorType, ContentLanguage, ContentMetadataContext, ContentReplyModule, DescriptiveTimerModule, Dialog, DialogButton, EFFECT_NAME, EFFECT_ORDER, EffectType, EventLayout, FCFSCouponModule, FloatingButtonModule, FloatingDirection, I18NextInjectionContext, ImageArea, ImageCarouselModule, ImageMapModule, ImageRailModule, ImageSlideModule, LayoutVariablesContext, LayoutVariablesContextDefaultValue, LayoutVariablesProvider, ModuleHierarchyContext, ModuleMapContext, ModuleMapProvider, ModuleMapper, ModuleRoot, ModuleTree, MoveDirection, OpenGraphHead, ProductCarouselModule, ProductFilterModule, ProductListModule, ProductRailModule, ProductTabModule, QuickArtworkTypeAModule, QuickArtworkTypeBModule, QuickBannerTypeAModule, QuickBannerTypeBModule, QuickBenefitTypeAModule, QuickBenefitTypeBModule, QuickCarouselTypeAModule, QuickCarouselTypeBModule, QuickStoreTypeAModule, RelatedContentModule, ScaleDirection, ScrollableTabAreaModule, ShortFormVideoModule, SingleImage, SiteId, SlotModule, StickerGroupModule, StoreGridModule, SwipeDirection, TabAreaModule, TabContentModule, TextEditorModule, ToastMessage, ToggleAreaModule, UnifiedSlotItemMapProvider, UserInputContext, VariantKey, VideoModule, YouTubeModule, dialog, getContentEditorItemCustomLink, getMergedModules, getPartialTabButtons, getPartialTabContents, getTabContentTree, getVisibleBetweenFilter, initializeModuleLandingScroll, isAtfCandidateModule, isContentReplyModule, isFloatingButtonModule, isImageCarouselModule, isImageMapModule, isImageModule, isImageRailModule, isImageSlideModule, isMaxImageAspectRatioModule, isProductCarouselModule, isProductFilterModule, isProductListModule, isProductModule, isProductTabModule, isScrollableTabAreaModule, isSlotModule, isStickerGroupModule, isStoreGridModule, isSumImageAspectRatioModule, isTabAreaModule, isTabContentModule, isTabModule, isVideoLikeModule, isVideoModule, isYouTubeModule, maxImageAspectRatio, mergeActionMapOptions, parseContentEditorItemPayload, parseContentEditorItemPayloadForBackoffice, setDialogComponent, setToastMessageComponent, sumImageAspectRatio, toastMessage, useAction, useComponentMap, useContainerWidth, useContentMetadata, useFixedImageHeight, useInjectedI18Next, useIsFixedImageModule, useLayoutVariables, useModuleHierarchy, useProgressiveQualityImage, useSafeArea, useSlotAction, useSlotImage, useTabLayout, useTimer, useUserInput, useUserInputState, useViewSizeBaseOnDeviceWidth };
